import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Spin,
  Switch,
  Button,
  Divider,
  Select,
  Table,
  InputNumber,
  Input,
  Modal,
  Popconfirm,
  Alert,
  Badge,
  message,
  Tooltip,
} from "antd";
import { DeleteOutlined, SortAscendingOutlined, SortDescendingOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuildingShield, faFingerprint } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { Ln1KeyMappingRow } from "./CdoLn1MappingEditor";
import {
  adapterProtocolName,
  deriveAdapterMatrixInputs,
  MESSAGE_CONTENT_TO_MATRIX_KEY,
} from "../../components/adapters/adapterMatrixInputs";
import type { AdapterMatrixInputs } from "../../components/adapters/adapterMatrixInputs";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import OpcVendorPathFeedback from "../../components/form/OpcVendorPathFeedback";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
// REDUX
import {
  usePostBankMutation,
  usePutBankMutation,
  useLazyGetBankQuery,
  useLazyGetBanksByServerQuery,
  useDeleteBankMutation,
  useDeleteBankFloorMutation,
  usePutBankPinAssignmentsMutation,
} from "../../../redux/banks/bankAPI";
import {
  useLazyGetChannelsByServerQuery,
  useLazyGetChannelQuery,
} from "../../../redux/channels/channelAPI";
import { messageTToCompositeKey } from "../../../flatbuffers/types/valuedMessageBuilder";
import { getDisplayName } from "../../../flatbuffers/types/ln2MessageOptions";
import unmappedHeartbeatCategory from "./heartbeatMappingCheck";
import {
  bankHasNoAssignedPins,
  countUnassignedPins,
  countUnresolvableLiftPins,
} from "./pinAssignmentCheck";
import {
  useLazyGetAdaptersQuery,
  useLazyGetAdapterQuery,
} from "../../../redux/adapters/adapterAPI";
import { useLazyGetAdapterKeysQuery } from "../../../redux/keys/keysAPI";
import {
  useLazyGetLiftsByBankQuery,
  usePostLiftMutation,
  useDeleteLiftMutation,
} from "../../../redux/lifts/liftAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import {
  OPC_PATH_PROTOCOLS,
  OPC_VENDOR_PATH_FIELDS,
  OPC_VENDOR_PATH_HELP_KEYS,
  OPC_VENDOR_PATH_LABELS,
  OpcVendorPathField,
  composeOpcUaNodePreview,
  firstLiftVendorPath,
  opcVendorPathTransition,
  readOpcVendorPathValues,
} from "../../../constants/opcVendorPaths";
import { useWizard } from "../WizardContext";
// STORE
import { RootState } from "../../../app/store";
// FLATBUFFERS
import { DevType } from "../../../flatbuffers/generated/front-end/dev-type";
import { formatUuidT } from "../../../flatbuffers/display";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { OverlayLifts } from "../../../flatbuffers/generated/archive/overlay-lifts";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { ChannelMode } from "../../../flatbuffers/generated/archive/channel-mode";
import { routableMismatchWarning } from "../../../constants/channelRouting";
import { channelTypeOf, numatoNeedsSession } from "../../../constants/numatoSession";
import { ChannelType } from "../../../flatbuffers/generated/archive/channel-type";
import { SessionProtocol } from "../../../flatbuffers/generated/session/session-protocol";
import { sessionConfigFrom } from "../components/ChannelSessionEditor";
import {
  logicalPortMax,
  logicalPortMin,
  logicalPortOutOfRange,
} from "../../../constants/bankLogicalPort";
import {
  OVERLAY_HELP,
  OVERLAY_LIFTS_LABEL,
  OVERLAY_TARGET_LABEL,
  overlayDrawnOnByMessage,
  overlaySaveFields,
  overlayTargetChoice,
  readBankOverlay,
} from "../../../constants/bankOverlay";
import ChannelModeBadge from "../components/ChannelModeBadge";
import DeletedBanksPanel from "./DeletedBanksPanel";
// ENTITY LINK
import WizardEntityLink from "../WizardEntityLink";
import WizardReadOnlyId from "../WizardReadOnlyId";
import WizardIdHoverTag from "../WizardIdHoverTag";
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
// SMART INVALIDATION
import {
  BANK_FIELD_RULES,
  computeInvalidatedSteps,
  WizardStep,
  wizardStepsFrom,
} from "../utils/wizardInvalidation";
import { resolveStandaloneBankScope } from "../utils/standaloneBankScope";
import {
  bankDoorSignatureFromRawFloors,
  bankDoorSignatureFromRows,
  resolveBankFloorDoorFields,
} from "../utils/bankDoorSignature";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
import { useWizardStepEntry } from "../utils/useWizardStepEntry";
import { describeError } from "../utils/describeError";
import { withBankAdapterLinks } from "../utils/bankAdapterLinks";
// PIN ASSIGNMENT MATRIX (Sequent adapters)
// PinAssignmentLift / PinAssignmentBankOption are owned by
// BankPinAssignmentMatrix.tsx — imported, never redefined, so this file
// always compiles against that component's actual contract.
import BankPinAssignmentMatrix, {
  PinAssignmentLift,
  PinAssignmentBankOption,
} from "./BankPinAssignmentMatrix";
import { DiscreteToLn2EntryInterface } from "../../../interfaces/Adapter.interface";
// NODE -> LIFT MATRIX (OPC UA adapters)
import BankNodeLiftMatrix from "./BankNodeLiftMatrix";
import nodeLiftMapToValidEntries from "../utils/opcUaNodeLiftMapping";
import { decodeOpcUaMapping } from "../../../flatbuffers/builders/opcUaCdoBuilder";
import {
  buildOpcUaNodeLiftIndexBindingsBytes,
  opcUaNodeLiftIndexBindingsToBase64,
  decodeOpcUaNodeLiftIndexBindings,
} from "../../../flatbuffers/builders/opcUaNodeLiftIndexBindingsBuilder";
import type { AdapterCdoOpcUa } from "../../../interfaces/AdapterCdoOpcUa.interface";
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";
import { coerceFlatBufferText } from "../../../utils/bankFloorOpenings";
import { getTableRowClass } from "../../../utils/utils";
// OFFSET MATRIX
import BankOffsetMatrix, {
  deriveSimplifiedBaseOffsets,
  MappedMessageType,
  AdapterKeyInfo,
} from "./BankOffsetMatrix";
import { migrateLegacyOffsetMap } from "../../components/adapters/offsetMatrixHelpers";
import {
  deserializeOffsetMapping,
  isCanonicalOffsetMapping,
} from "../../components/adapters/AdapterEditor";
import { matchLegacyEntryToColumn } from "../../components/adapters/legacySubTypeNormalizer";
import type { AdapterColumnDescriptor } from "../../components/adapters/adapterColumnTypes";

/**
 * Normalize the offset matrix for persistence.
 *
 * The UI state can accumulate register-type keys (from the "By Register Type"
 * view) or drop category keys (if a toggle left them unset), producing mixed
 * or partial maps. At the save boundary we project each lift row onto the
 * authoritative set of message-category keys from mappedTypes. Resolution
 * order for each category key:
 *   1. direct value under the category key
 *   2. fall back to the value under the mapped register-type key (so saves
 *      from the "By Register Type" view still persist the user's intent
 *      even when the toggle wasn't switched off first)
 *   3. default to 0
 * Keys not in mappedTypes (e.g. "holding_register" itself) are never
 * emitted. This guarantees modbus_expansion.go lookupCategoryOffset finds
 * every category it looks up, preventing per-lift registers from collapsing
 * onto a shared address.
 */
export function liftOffsetsToKeyedMaps(
  offsets: Record<string, number>[],
  mappedTypes: MappedMessageType[],
  categoryToRegType?: Record<string, string>,
): Record<string, number>[] {
  // Emit OVERRIDES ONLY — keys that have an explicit stored value. Cells
  // without a stored value are omitted so the bank's saved offset_mapping
  // stays sparse; on reload they re-derive from the adapter's
  // initial+delta defaults (merged in StepBanks on bank load).
  //
  // For merged (intra-content) columns, every member participates. The
  // BankOffsetMatrix cell write already fans values out to each member key
  // (`service`, `usage`, …), so we must emit every member here — not just
  // the column's primary `categoryKey` — or the save would drop the
  // non-primary members and leave the server's per-category offset_mapping
  // arrays stale for those categories.
  return offsets.map((row) => {
    const normalized: Record<string, number> = {};
    mappedTypes.forEach((mt) => {
      const memberKeys =
        mt.categoryKeys && mt.categoryKeys.length > 0 ? mt.categoryKeys : [mt.categoryKey];
      let anyEmitted = false;
      memberKeys.forEach((member) => {
        if (row && member in row) {
          normalized[member] = row[member];
          anyEmitted = true;
        }
      });
      if (anyEmitted) return;
      // Register-type proxy: value stored under the register type (e.g.
      // "holding_register"), emit it under the column's primary key so the
      // saved bank is keyed consistently with the LN2 category set.
      const regType = categoryToRegType?.[mt.categoryKey];
      if (regType && row && regType in row) {
        normalized[mt.categoryKey] = row[regType];
      }
    });
    return normalized;
  });
}

/**
 * Resize a lift-offset matrix to `count` rows.
 *
 * Two row-fill strategies — preferred order:
 *
 *   1. **Adapter-derived (when `adapter` is provided)**: re-derive the full
 *      `count`-row baseline from the adapter's `offset_mapping`. Any row in
 *      the input matrix that the user has already edited (i.e. differs from
 *      the adapter default) is preserved verbatim; new rows inherit the
 *      adapter's per-unit stride. This is the correct behavior when the
 *      user grows the bank from 1 lift to N — without it, rows 2..N-1 all
 *      collapse to row 0 because the stride inferred from a single-row
 *      matrix is 0.
 *
 *   2. **Stride extrapolation (fallback)**: when no adapter is available
 *      (legacy callers, bank load before adapter resolved, edge cases), use
 *      the row-1-minus-row-0 stride to extrapolate. A 1-row input yields a
 *      stride of 0 and clones row 0 — which is what produced the original
 *      bug, so callers should pass `adapter` whenever possible.
 *
 * The function never shrinks below `count` and never returns rows referencing
 * stale columns from a prior `mappedTypes` set.
 */
export function resizeLiftOffsetRows(
  rows: Record<string, number>[],
  count: number,
  types: MappedMessageType[],
  adapter?: unknown,
): Record<string, number>[] {
  const colKeys = types.map((mt) => mt.categoryKey);

  // Path 1: adapter-derived re-baseline. We compute the FULL `count`-row
  // adapter baseline up front, then overlay each existing row's values for
  // every column the row actually has data for. The overlay preserves user
  // edits (cells that differ from the adapter default) without re-applying
  // any zero-fill from the adapter side.
  if (adapter !== undefined && adapter !== null) {
    const adapterBaseline = deriveOffsetsFromAdapter(adapter, types, count);
    if (adapterBaseline.length === count) {
      return adapterBaseline.map((baseRow, i) => {
        const existing = rows[i];
        if (!existing) return { ...baseRow };

        // Overlay only columns the existing row actually had — any column
        // newly introduced via `mappedTypes` shows the adapter default.
        const merged: Record<string, number> = { ...baseRow };
        colKeys.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(existing, key)) {
            merged[key] = existing[key];
          }
        });
        return merged;
      });
    }
  }

  // Path 2 (fallback): stride extrapolation. Reproduces the pre-fix behavior
  // for callers without an adapter handle. Documented limitation: a 1-row
  // input produces a 0 stride; if the caller wants per-unit strides they MUST
  // pass `adapter`.
  const updated = rows.map((row) => ({ ...row }));
  const strides: Record<string, number> = {};

  colKeys.forEach((categoryKey) => {
    strides[categoryKey] =
      updated.length >= 2 ? (updated[1]?.[categoryKey] ?? 0) - (updated[0]?.[categoryKey] ?? 0) : 0;
  });

  while (updated.length < count) {
    const previousRow = updated[updated.length - 1] || {};
    const nextRow: Record<string, number> = {};
    colKeys.forEach((categoryKey) => {
      nextRow[categoryKey] = (previousRow[categoryKey] ?? 0) + strides[categoryKey];
    });
    updated.push(nextRow);
  }

  return updated.slice(0, count);
}

export function resolveSimplifiedBaseline(
  adapterDefaults: Record<string, number>[],
  fallbackOffsets: Record<string, number>[],
): Record<string, number>[] {
  if (adapterDefaults.length > 0) {
    return adapterDefaults.map((row) => ({ ...row }));
  }
  // No per-unit adapter defaults: return zero-filled rows using the key structure
  // from fallbackOffsets so that deriveSimplifiedBaseOffsets correctly recovers
  // the user-entered base offsets as the delta from zero rather than collapsing
  // every delta to 0 (which happened when stored offsets were used as the baseline).
  return fallbackOffsets.map((row) => Object.fromEntries(Object.keys(row).map((k) => [k, 0])));
}

/**
 * Decode a bank's persisted `opc_ua_node_lift_mapping_binary` back into the
 * matrix UI's Record<nodeKey, liftIndexString>. FlatBuffers-only — there is
 * no JSON twin. Returns {} for missing/malformed input rather than throwing,
 * so a bank with no OPC UA binding yet (or a decode failure) just renders an
 * empty matrix.
 *
 * Two shapes reach here depending on how the response was decoded: a base64
 * string on the legacy JSON path (Go's encoding/json marshals a []byte
 * column that way), or a raw `number[]` byte array when the wizard's
 * FlatBuffer response path (`flatBufferBaseQuery`, used by every bankAPI
 * call) unpacks BankT's `opcUaNodeLiftMappingBinary` field directly. Every
 * other binary bank field sidesteps this by reading a JSON-string twin
 * instead of its raw bytes — this is the one field with no twin, so it is
 * the one place this has to be handled. Rejecting the array shape here
 * previously made the matrix always render empty on load, even for a bank
 * whose binding saved correctly.
 */
function decodeOpcUaNodeLiftMappingBinary(rawBinary: unknown): Record<string, string> {
  try {
    if (typeof rawBinary === "string" && rawBinary !== "") {
      return decodeOpcUaNodeLiftIndexBindings(
        Uint8Array.from(atob(rawBinary), (c) => c.charCodeAt(0)),
      );
    }
    if (Array.isArray(rawBinary) && rawBinary.length > 0) {
      return decodeOpcUaNodeLiftIndexBindings(Uint8Array.from(rawBinary));
    }
  } catch {
    return {};
  }
  return {};
}

/**
 * Parse stored offset data into keyed maps.
 * Handles both new keyed format and legacy positional arrays.
 */
function keyedMapsToLiftOffsets(
  stored: unknown,
  types: MappedMessageType[],
): Record<string, number>[] {
  if (!Array.isArray(stored) || stored.length === 0) return [];

  // New keyed format: [{"service": 9, "traffic": 7}, ...]
  if (typeof stored[0] === "object" && !Array.isArray(stored[0])) {
    return stored as Record<string, number>[];
  }

  // Legacy positional format: [[9, 7, 8], ...] — convert to keyed
  return (stored as number[][]).map((row) => {
    const keyed: Record<string, number> = {};
    types.forEach((mt, colIdx) => {
      keyed[mt.categoryKey] = row[colIdx] ?? 0;
    });
    return keyed;
  });
}

/** MessageContent discriminant → category key.
 *
 *  The shared table, derived from the generated `MessageContent` enum rather
 *  than written out here, so a category added to the schema reaches this screen
 *  without anyone remembering to add it. */
const MC_TO_KEY = MESSAGE_CONTENT_TO_MATRIX_KEY;

/**
 * Protocols that use pin-to-lift assignment instead of register offsets.
 *
 * The members are read back out of `AdapterProtocol` rather than typed, because
 * the values compared against this set come from `AdapterProtocol[n]` — the two
 * Numato entries were previously spelled `NUMATO_GPIO`/`NUMATO_RELAY`, which the
 * enum has never produced, so neither board was ever offered the pin matrix.
 */
const PIN_ASSIGNMENT_PROTOS = new Set([
  AdapterProtocol[AdapterProtocol.SEQUENT],
  AdapterProtocol[AdapterProtocol.NUMATOGPIO],
  AdapterProtocol[AdapterProtocol.NUMATORELAY],
]);

/**
 * Map offset_mapping message_type IDs to category keys.
 *
 * The adapter offset_mapping uses the old message_types table IDs,
 * NOT the FlatBuffer MessageContent enum. The numbering differs
 * significantly:
 *
 *   message_types table:  0=CONTROL, 2=FAULT, 4=GROUPSUPERVISORY,
 *     5=SERVICE, 6=STATUS, 7=TRAFFIC, 8=USAGE
 *
 *   MessageContent enum:  1=Status, 2=Traffic, 3=Usage, 4=Service,
 *     5=Control, 6=Fault, 7=GroupSupervisory, 8=GroupControl
 *
 * This map uses the message_types table numbering since that's what
 * the adapter offset_mapping stores.
 */
const OFFSET_MT_TO_KEY: Record<number, string> = {
  0: "control",
  2: "fault",
  4: "group_supervisory_status",
  5: "service",
  6: "status",
  7: "traffic",
  8: "usage",
};

/**
 * Map a MODBUS bank-matrix register-type column key (e.g. "coil",
 * "discrete_input") to the `register_offsets` table field pair that
 * carries its Initial offset and per-lift Delta. The table is the FB
 * schema's `ModbusRegisterOffsetMap` flat representation; this helper is
 * the bridge from the bank UI's column key set to that 8-field shape.
 *
 * Keys here are the canonical strings from `REGISTER_TYPE_KEY` in
 * BankOffsetMatrix.tsx — kept in sync with that module so a refactor
 * there is caught immediately by the offset-derivation tests.
 */
const MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS: Record<
  string,
  { initialKey: string; deltaKey: string }
> = {
  coil: { initialKey: "coil_initial", deltaKey: "coil_delta" },
  discrete_input: { initialKey: "di_initial", deltaKey: "di_delta" },
  holding_register: { initialKey: "hr_initial", deltaKey: "hr_delta" },
  input_register: { initialKey: "ir_initial", deltaKey: "ir_delta" },
  // The synthetic WRITE_REG column covers write-side register access; on
  // wire-level Modbus that's the Holding-Register space, so reuse hr_*.
  write_register: { initialKey: "hr_initial", deltaKey: "hr_delta" },
};

/**
 * Map a Modbus Request/Response union discriminant (Type field) to the
 * `MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS` column key for the corresponding
 * register space. Mirrors `UNION_DISC_TO_REG_TYPE` inside
 * BankOffsetMatrix.tsx's extractModbusRegisterTypeMappedTypes — duplicated
 * here so the StepBanks derivation has no circular dependency on the
 * matrix module's internal helper.
 */
const MODBUS_UNION_DISC_TO_BANK_COL: Record<number, string> = {
  1: "coil", // ReadCoils*
  2: "discrete_input", // ReadDiscreteInputs*
  3: "holding_register", // ReadHoldingRegisters*
  4: "input_register", // ReadInputRegisters*
  5: "holding_register", // ReadWriteMultiple*
  6: "coil", // WriteSingleCoil*
  7: "coil", // WriteMultipleCoils*
  8: "write_register", // WriteSingleRegister*
  9: "write_register", // WriteMultipleRegisters*
};

/**
 * Resolve a Modbus read/write entry's register-space column key from any
 * of the shapes the cdo_template JSON can take. Tries (in order) the FB
 * Object API `readType`/`writeType` enum, then the snake_case
 * `read_type`/`write_type` enum, then the union discriminant on
 * `request.Type` / `response.Type`. Returns null when nothing maps.
 */
/** Pick the first numeric value across the two field-name spellings the
 *  cdo_template entry may use (camelCase from the FB Object API JSON,
 *  snake_case from a hand-written wizard payload). Returns null when
 *  neither field carries a finite number. */
function pickNumericField(entry: any, camelKey: string, snakeKey: string): number | null {
  if (typeof entry?.[camelKey] === "number") return entry[camelKey];
  if (typeof entry?.[snakeKey] === "number") return entry[snakeKey];
  return null;
}

/** Read the request/response union discriminant from the legacy
 *  wrapped shape, regardless of casing. */
function legacyUnionDiscriminant(entry: any): number | null {
  const candidates = [
    entry?.request?.Type,
    entry?.request?.type,
    entry?.response?.Type,
    entry?.response?.type,
  ];
  const match = candidates.find((c) => typeof c === "number");
  return typeof match === "number" ? match : null;
}

const MODBUS_READ_TYPE_TO_BANK_COL: Record<number, string> = {
  0: "holding_register", // ReadHoldingRegisters
  1: "coil", // ReadCoils
  2: "discrete_input", // ReadDiscreteInputs
  3: "input_register", // ReadInputRegisters
};

const MODBUS_WRITE_TYPE_TO_BANK_COL: Record<number, string> = {
  0: "write_register", // WriteSingleRegister
  1: "coil", // WriteSingleCoil
  2: "write_register", // WriteMultipleRegisters
  3: "coil", // WriteMultipleCoils
  4: "write_register", // MaskWriteRegister
};

function resolveModbusEntryColumn(entry: any): string | null {
  if (!entry || typeof entry !== "object") return null;
  const readType = pickNumericField(entry, "readType", "read_type");
  if (readType !== null && MODBUS_READ_TYPE_TO_BANK_COL[readType]) {
    return MODBUS_READ_TYPE_TO_BANK_COL[readType];
  }
  const writeType = pickNumericField(entry, "writeType", "write_type");
  if (writeType !== null && MODBUS_WRITE_TYPE_TO_BANK_COL[writeType]) {
    return MODBUS_WRITE_TYPE_TO_BANK_COL[writeType];
  }
  // Legacy request/response wrapper with a union discriminant.
  const reqType = legacyUnionDiscriminant(entry);
  if (reqType !== null && MODBUS_UNION_DISC_TO_BANK_COL[reqType]) {
    return MODBUS_UNION_DISC_TO_BANK_COL[reqType];
  }
  return null;
}

/**
 * Extract the categoryKey → bank-column mapping for a MODBUS adapter by
 * walking its cdo_template reads/writes. Used to translate LN2-keyed
 * bank-matrix columns back to the register space their adapter mapping
 * actually targets, so `deriveOffsetsFromAdapter` can write into the
 * right LN2 categoryKey while sourcing values from the register_offsets
 * map. Returns an empty map when no entries can be resolved.
 */
function buildCategoryKeyToBankColumnMap(cdoTemplate: any): Record<string, string> {
  if (!cdoTemplate || typeof cdoTemplate !== "object") return {};
  const out: Record<string, string> = {};
  const collect = (entries: unknown) => {
    if (!Array.isArray(entries)) return;
    entries.forEach((entry: any) => {
      // Three on-the-wire shapes for a cdo_template read/write entry:
      //   1. EditorModbusTemplate (frontend save path): `msgId` is a
      //      composite key like "control:normal:1". The category prefix
      //      before the first colon is the LN2 categoryKey directly.
      //   2. FlatBuffer Object API: nested `ln2_message.msg.content`
      //      union carrying a numeric MessageContent discriminant.
      //   3. Same shape with camelCase `ln2Message` — depending on
      //      whether the response came through `parseFbJson` or the
      //      raw Object API.
      // Prefer the composite-key shape because the editor uses it
      // verbatim; fall back to the FB shape for older rows hydrated
      // from cdo_binary.
      let categoryKey: string | null = null;
      const msgId = entry?.msgId;
      if (typeof msgId === "string" && msgId.length > 0) {
        const prefix = msgId.split(":")[0];
        if (prefix) categoryKey = prefix;
      }
      if (!categoryKey) {
        const ln2 = entry?.ln2Message ?? entry?.ln2_message;
        if (ln2?.msg?.content) {
          const contentType = pickNumericField(ln2.msg.content, "Type", "type");
          if (contentType !== null && CONTENT_TYPE_TO_KEY[contentType]) {
            categoryKey = CONTENT_TYPE_TO_KEY[contentType];
          }
        }
      }
      if (!categoryKey) return;
      const column = resolveModbusEntryColumn(entry);
      if (column) out[categoryKey] = column;
    });
  };
  collect(cdoTemplate?.reads);
  collect(cdoTemplate?.writes);
  return out;
}

/**
 * Reverse mapping of the Ln2.MessageContent union discriminant value
 * (defined in flatbuffers/ln2/message-content.fbs) onto the matrix's
 * LN2 categoryKey constants. Kept local to avoid pulling in the heavy
 * `messageContentToCategoryKey` helper just for this static table.
 */
const CONTENT_TYPE_TO_KEY: Record<number, string> = {
  1: "status",
  2: "traffic",
  3: "usage",
  4: "service",
  5: "control",
  6: "fault",
  7: "group_supervisory_status",
  8: "group_control",
  9: "controller_info",
};

/**
 * Normalize a `register_offsets` value (any of the three on-the-wire shapes
 * — canonical snake_case flat map, FB-class camelCase flat map, or the
 * legacy nested {initial,delta} encoding) into the canonical flat map
 * `{coil_initial, coil_delta, di_initial, ...}`. Returns null when no
 * recognizable offsets data is present so callers can fall back to a
 * zero-fill baseline.
 */
function normalizeModbusRegisterOffsetsForDerive(
  cdoTemplate: unknown,
): Record<string, number> | null {
  if (!cdoTemplate || typeof cdoTemplate !== "object") return null;
  const obj = cdoTemplate as Record<string, unknown>;

  const tryFlatSnake = obj.register_offsets as Record<string, unknown> | undefined;
  if (tryFlatSnake && typeof tryFlatSnake === "object" && !Array.isArray(tryFlatSnake)) {
    if ("coil_initial" in tryFlatSnake || "hr_initial" in tryFlatSnake) {
      const flat: Record<string, number> = {};
      Object.entries(tryFlatSnake).forEach(([k, v]) => {
        flat[k] = Number(v ?? 0);
      });
      return flat;
    }
    if ("initial" in tryFlatSnake || "delta" in tryFlatSnake) {
      return flattenNestedRegisterOffsets(tryFlatSnake);
    }
  }

  const tryFlatCamel = obj.registerOffsets as Record<string, unknown> | undefined;
  if (tryFlatCamel && typeof tryFlatCamel === "object" && !Array.isArray(tryFlatCamel)) {
    if ("coilInitial" in tryFlatCamel || "hrInitial" in tryFlatCamel) {
      return {
        coil_initial: Number(tryFlatCamel.coilInitial ?? 0),
        coil_delta: Number(tryFlatCamel.coilDelta ?? 0),
        di_initial: Number(tryFlatCamel.diInitial ?? 0),
        di_delta: Number(tryFlatCamel.diDelta ?? 0),
        hr_initial: Number(tryFlatCamel.hrInitial ?? 0),
        hr_delta: Number(tryFlatCamel.hrDelta ?? 0),
        ir_initial: Number(tryFlatCamel.irInitial ?? 0),
        ir_delta: Number(tryFlatCamel.irDelta ?? 0),
      };
    }
    if ("initial" in tryFlatCamel || "delta" in tryFlatCamel) {
      return flattenNestedRegisterOffsets(tryFlatCamel);
    }
  }

  const legacy = obj.modbusRegisterOffsets;
  if (legacy && typeof legacy === "object" && !Array.isArray(legacy)) {
    return flattenNestedRegisterOffsets(legacy as Record<string, unknown>);
  }

  return null;
}

/** Convert the legacy `{initial:{COIL:N},delta:{COIL:N}}` shape to the flat
 *  `{coil_initial, coil_delta, ...}` shape. Missing register types default to 0.
 */
function flattenNestedRegisterOffsets(nested: Record<string, unknown>): Record<string, number> {
  const initial = (nested.initial as Record<string, unknown>) ?? {};
  const delta = (nested.delta as Record<string, unknown>) ?? {};
  return {
    coil_initial: Number(initial.COIL ?? 0),
    coil_delta: Number(delta.COIL ?? 0),
    di_initial: Number(initial.DI ?? 0),
    di_delta: Number(delta.DI ?? 0),
    hr_initial: Number(initial.HR ?? 0),
    hr_delta: Number(delta.HR ?? 0),
    ir_initial: Number(initial.IR ?? 0),
    ir_delta: Number(delta.IR ?? 0),
  };
}

/**
 * Derive a per-lift bank-matrix baseline for a MODBUS adapter using the
 * adapter's register-level Initial+Delta offsets. Each output row is
 *   row[columnKey] = initial + liftIndex * delta
 * for every column in `types`. The mapping from a column's LN2
 * categoryKey to the register space it targets is rebuilt from the
 * adapter's cdo_template reads/writes; columns whose register space
 * has no Initial/Delta configured pass through as 0.
 *
 * The function also handles the case where `types` carries the
 * register-type-keyed columns directly (coil, holding_register, …) —
 * this is what `extractModbusRegisterTypeMappedTypes` emits when the
 * bank matrix runs in by-register-type mode. The lookup falls through
 * to MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS without needing the
 * categoryKey → register space translation table.
 *
 * Returns an empty array when no register-level offsets are configured
 * on the adapter — callers fall back to the zero baseline path
 * (existing behavior for adapters that don't expand).
 */
function deriveModbusBankOffsets(
  adapter: any,
  types: MappedMessageType[],
  numLifts: number,
): Record<string, number>[] {
  const cdoTemplate = adapter?.cdo_template ?? adapter?.cdoTemplate ?? null;
  // cdo_template may arrive as a JSON string when the adapter row came
  // from a list endpoint that returns the raw column. Parse before
  // shape-detecting so the helper above sees a real object.
  let cdoParsed: unknown = cdoTemplate;
  if (typeof cdoTemplate === "string") {
    try {
      cdoParsed = JSON.parse(cdoTemplate);
    } catch {
      cdoParsed = null;
    }
  }
  const flat = normalizeModbusRegisterOffsetsForDerive(cdoParsed);
  if (!flat) return [];

  const categoryKeyToBankCol = buildCategoryKeyToBankColumnMap(cdoParsed);

  const matrix: Record<string, number>[] = [];
  for (let liftIdx = 0; liftIdx < numLifts; liftIdx += 1) {
    const row: Record<string, number> = {};
    types.forEach((mt) => {
      const columnKey = mt.categoryKey;
      // Two key shapes flow through: register-type keys (coil,
      // holding_register, …) when extractModbusRegisterTypeMappedTypes
      // built `types`, and LN2 categoryKey (status, traffic, …) when
      // extractMappedMessageTypes did. Both resolve to the same
      // register-offsets field pair via the lookup table or the
      // adapter-derived translation map.
      const directFields = MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS[columnKey];
      const translatedColumn = categoryKeyToBankCol[columnKey];
      const resolvedFields =
        directFields ??
        (translatedColumn ? MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS[translatedColumn] : undefined);
      if (!resolvedFields) {
        row[columnKey] = 0;
        return;
      }
      const initial = flat[resolvedFields.initialKey] ?? 0;
      const delta = flat[resolvedFields.deltaKey] ?? 0;
      const value = initial + liftIdx * delta;
      row[columnKey] = value;
      // The bank matrix in byRegisterType mode renders columns keyed by
      // register-type (e.g. "write_register") and reads liftDict[key]
      // directly — BankOffsetMatrix's resolveMemberKeys only fans out
      // multi-member columns, so a register-type column with a single
      // LN2 member cannot fall back to the LN2 key. Mirror the value
      // under the register-type key whenever we translated from LN2
      // so both rendering modes find the offset on the row.
      if (translatedColumn && translatedColumn !== columnKey) {
        row[translatedColumn] = value;
      }
    });
    matrix.push(row);
  }
  return matrix;
}

/**
 * Numeric FlatBuffer `AdapterProtocol` discriminant → protocol string,
 * used when the API hands back the typed enum (FB Object API) rather
 * than the human-readable string column.
 *
 * Derived directly from the generated `AdapterProtocol` enum (reverse
 * lookup via `AdapterProtocol[value]`) instead of a hand-maintained
 * table. The previous hand-rolled table drifted from the wire enum
 * (e.g. mapped numeric 9 → "OPC" when the schema's actual OPC=8 and
 * 9=SEQUENT, cascading an off-by-one through every entry from 9
 * onward, and invented a nonexistent "OTIS" protocol at 14 instead of
 * the real LOBBYVISION=14) — silently mislabeling every adapter whose
 * protocol arrived as a numeric enum rather than a string. Building
 * this from the generated enum means a future schema addition (like
 * OPCUA=16) is automatically correct with no manual table edit.
 */
/**
 * Resolve a protocol type string from an arbitrary adapter shape. The
 * backend may surface it as a snake_case string (`protocol_type`), a
 * camelCase string (`protocolType`), or as a numeric FlatBuffer enum
 * value when the typed Object API path lands in state. All three are
 * normalized to a single uppercase string so callers can match on
 * `"MODBUS"` without worrying about which serialization fired. The
 * string column wins over the enum because the string already encodes
 * the canonical name — defense-in-depth against a future enum-table
 * addition silently aliasing onto the wrong protocol.
 *
 * Previously this used `??` to coalesce, but `protocolType` on FB Object
 * API responses arrives as a TRUTHY number (`1` for MODBUS) and short-
 * circuited the chain before `protocol_type` (the canonical string) was
 * considered — `String(1).toUpperCase()` returned `"1"`, breaking every
 * downstream `=== "MODBUS"` branch. The explicit type checks below
 * recover the canonical string regardless of which serializer ran.
 */
function resolveAdapterProtocolUpper(adapter: any): string {
  return adapterProtocolName(adapter);
}

/**
 * The protocol string LiftCloud's `guardOpcUaChannelEndpoint` keys on — the
 * normalised form `resolveAdapterProtocolUpper` produces for
 * `AdapterProtocol.OPCUA`, whether the adapter arrived as the FB enum, a
 * camelCase string or a snake_case one.
 */
export const OPC_UA_PROTOCOL = "OPCUA";

/**
 * Whether this bank pairs an OPC UA adapter with a channel that cannot tell
 * the OPC UA server where it is, which LiftCloud refuses with a 400.
 *
 * `guardOpcUaChannelEndpoint` (bank.controller.go) rejects the save when the
 * adapter's protocol is OPCUA and the bound channel's `opc_ua_endpoint_url` is
 * null or blank: OPC UA addresses by URL, and `resolveOpcUaSocket` reads that
 * column and nothing else, so the bank could never produce a CDO. The bank step
 * is the first point where both halves of the pairing are known, so this is
 * where it can be said — better here than as an opaque server error after the
 * operator presses Save.
 *
 * Blank-tolerant on purpose: a URL of only whitespace passes the same way the
 * server's own `strings.TrimSpace` check does.
 */
export function opcUaChannelEndpointMissing(args: {
  adapterProtocol: string;
  channelOpcUaEndpointUrl: string | undefined;
}): boolean {
  if (args.adapterProtocol !== OPC_UA_PROTOCOL) return false;
  return (args.channelOpcUaEndpointUrl ?? "").trim() === "";
}

/**
 * Derive initial lift offsets from the adapter's per-unit offset_mapping
 * entries.  Used when the bank has no saved offset_mapping of its own.
 *
 * The adapter stores entries like:
 *   {offset: 760, unit_number: 0, message_type: 7}                     ← category (TRAFFIC)
 *   {offset: 1360, unit_number: 0, message_type: 0, message_sub_type: "EnergizeCarCall"}  ← individual (CONTROL)
 *
 * Category entries are matched to category columns by converting
 * message_type → categoryKey via OFFSET_MT_TO_KEY. Entries with
 * message_sub_type that don't have a matching category-only entry
 * are also used for the category column.
 *
 * For MODBUS adapters, the function delegates to `deriveModbusBankOffsets`
 * which uses the adapter's `register_offsets` map (Initial+Delta per
 * register space) to compute `initial + liftIdx * delta` for every lift.
 *
 * Returns a number[][] of [liftIndex][colIndex] = offset value.
 */
export function deriveOffsetsFromAdapter(
  adapter: any,
  types: MappedMessageType[],
  numLifts: number,
): Record<string, number>[] {
  // MODBUS adapters use register-level Initial/Delta (Coil, DI, HR, IR)
  // instead of MessageContent-keyed offset_mapping entries. Delegate
  // before we even try to parse offset_mapping — for MODBUS that field
  // is empty and falling through would return [] anyway.
  if (resolveAdapterProtocolUpper(adapter) === "MODBUS") {
    return deriveModbusBankOffsets(adapter, types, numLifts);
  }

  const rawOm = adapter?.offset_mapping;
  // offset_mapping can arrive in three shapes:
  //   1. `[]OffsetMappingEntry` array (current canonical format)
  //   2. `{ "<mc>": [initial, initial+delta] }` legacy object, keyed by
  //      MessageContent enum value (written by older AdapterEditor versions)
  //   3. JSON string wrapping either of the above (FlatBuffer responses)
  // Parse string first, then branch on array vs object.
  let parsed: unknown = rawOm;
  if (typeof rawOm === "string") {
    try {
      parsed = JSON.parse(rawOm);
    } catch {
      parsed = null;
    }
  }

  let entries: any[] = [];
  let forceCanonicalMapping = false;
  if (Array.isArray(parsed)) {
    entries = parsed;
  } else if (parsed && typeof parsed === "object") {
    // Legacy object format: convert each {"<mc>": [initial, initial+delta]}
    // into two positional OffsetMappingEntry records so the rest of this
    // function can treat everything uniformly. Keys are already FlatBuffer
    // MessageContent enum values, so pin the mapping table to MC_TO_KEY.
    Object.entries(parsed as Record<string, unknown>).forEach(([mcStr, pair]) => {
      const mc = Number(mcStr);
      if (!Number.isFinite(mc) || mc <= 0 || mc > 9) return;
      if (!Array.isArray(pair)) return;
      (pair as unknown[]).forEach((offset, unit) => {
        if (typeof offset !== "number" || !Number.isFinite(offset)) return;
        entries.push({ message_type: mc, unit_number: unit, offset });
      });
    });
    forceCanonicalMapping = true;
  }
  if (entries.length === 0 || types.length === 0) return [];

  // Build set of valid category keys for quick lookup
  const validKeys = new Set(types.map((mt) => mt.categoryKey));

  // Decide which id table `message_type` is read through. Canonical entries
  // carry FlatBuffer MessageContent ids (Traffic=2, Control=5, ...); legacy ones
  // the old message_types-table ids (Traffic=7, Control=0, ...).
  //   - A "normal"/"valued" sub_type (or the legacy-object shape) is written
  //     only by the current encoding: canonical.
  //   - A message_type 0, or a friendly-name sub_type, is legacy-only.
  //   - Anything else — grouped categories, which carry no sub_type — reads
  //     the same in both encodings, so ids alone cannot say which one wrote it.
  //     The adapter editor writes exactly this shape today, and the per-column
  //     defaults the matrix falls back to read it as canonical; but pre-schema
  //     imports produced it too. Whichever table lands more entries on columns
  //     this adapter actually has wins, and a tie goes to canonical so the
  //     seeded cells agree with those defaults. Reading it as legacy
  //     unconditionally zeroed every column of a canonical grouped-only
  //     adapter, and those zeros then beat the adapter's own offsets.
  const usesCanonicalFormat = (() => {
    if (
      forceCanonicalMapping ||
      entries.some(
        (entry: any) =>
          entry?.message_sub_type === "normal" || entry?.message_sub_type === "valued",
      )
    ) {
      return true;
    }
    if (!isCanonicalOffsetMapping(entries)) return false;
    const landsOnColumn = (table: Record<number, string>) =>
      entries.filter((entry: any) => validKeys.has(table[entry?.message_type ?? -1])).length;
    return landsOnColumn(MC_TO_KEY) >= landsOnColumn(OFFSET_MT_TO_KEY);
  })();
  const mtToKey = usesCanonicalFormat ? MC_TO_KEY : OFFSET_MT_TO_KEY;

  // Initialize zero-filled keyed maps
  const matrix: Record<string, number>[] = Array.from({ length: numLifts }, () => {
    const row: Record<string, number> = {};
    types.forEach((mt) => {
      row[mt.categoryKey] = 0;
    });
    return row;
  });

  // Track which (category, unit) pairs have been populated by a
  // sub_type-free entry so we prefer those over sub_typed ones.
  const populated = new Set<string>();

  // Every write goes through here so the columns the adapter actually
  // addresses, and at which units, are known once the entries are consumed.
  const written = new Set<string>();
  const setCell = (unitIdx: number, key: string, offset: number): void => {
    matrix[unitIdx][key] = offset;
    written.add(`${key}\u0000${unitIdx}`);
  };

  // First pass: entries WITHOUT message_sub_type (pure category entries)
  entries.forEach((entry: any) => {
    const unitIdx = entry.unit_number ?? 0;
    if (unitIdx >= numLifts) return;
    const subType = entry.message_sub_type;
    if (subType && subType !== "") return; // skip sub_type entries in first pass

    const catKey = mtToKey[entry.message_type ?? -1];
    if (!catKey || !validKeys.has(catKey)) return;

    setCell(unitIdx, catKey, entry.offset ?? 0);
    populated.add(`${catKey}:${unitIdx}`);
  });

  // Second pass: entries WITH message_sub_type — either the new canonical
  // variant marker ("normal"|"valued" + message_value), or a legacy friendly
  // name like "CarCall" that has to be matched against the column label.
  entries.forEach((entry: any) => {
    const unitIdx = entry.unit_number ?? 0;
    if (unitIdx >= numLifts) return;
    const subType = entry.message_sub_type;
    if (!subType || subType === "") return; // already handled

    const catKey = mtToKey[entry.message_type ?? -1];

    // New canonical path: AdapterEditor emits `message_sub_type: "normal"|"valued"`
    // alongside a numeric `message_value`. Build the composite categoryKey
    // directly (e.g. "control:normal:15") so the matching is exact and
    // survives server round-trips.
    if ((subType === "normal" || subType === "valued") && typeof entry.message_value === "number") {
      if (catKey) {
        const compositeKey = `${catKey}:${subType}:${entry.message_value}`;
        if (validKeys.has(compositeKey)) {
          setCell(unitIdx, compositeKey, entry.offset ?? 0);
          return; // handled — skip the legacy/grouped fallbacks
        }
      }
    }

    // Legacy fallback — sub_type is a friendly name (e.g. "CarCall", "Heartbeat"),
    // or points at a grouped category when no individual column matches.
    // Delegate to the shared normalizer for consistent matching across all callers.
    const legacyColumns: AdapterColumnDescriptor[] = types.map((t) => ({
      key: t.categoryKey,
      label: t.label,
      isIndividual: Boolean(t.isIndividual),
    }));
    const matchResult = matchLegacyEntryToColumn(
      {
        offset: entry.offset ?? 0,
        unit_number: unitIdx,
        message_type: entry.message_type ?? -1,
        message_sub_type: subType,
        message_value: typeof entry.message_value === "number" ? entry.message_value : undefined,
      },
      legacyColumns,
    );

    if (matchResult.columnKey !== null && validKeys.has(matchResult.columnKey)) {
      const resolvedKey = matchResult.columnKey;
      if (resolvedKey.includes(":")) {
        // Individual composite column
        setCell(unitIdx, resolvedKey, entry.offset ?? 0);
      } else if (!populated.has(`${resolvedKey}:${unitIdx}`)) {
        // Grouped category column
        setCell(unitIdx, resolvedKey, entry.offset ?? 0);
        populated.add(`${resolvedKey}:${unitIdx}`);
      }
    } else if (catKey && validKeys.has(catKey) && !populated.has(`${catKey}:${unitIdx}`)) {
      // Final fallback: use the raw catKey if the normalizer found nothing
      setCell(unitIdx, catKey, entry.offset ?? 0);
      populated.add(`${catKey}:${unitIdx}`);
    }
  });

  // The adapter editor saves a fixed number of units (8 by default), so a bank
  // with more cars than that finds no entry for the rest. Left as the zero
  // fill they would be stored as explicit overrides that beat the adapter's own
  // initial + car * delta, stacking those cars on address 0. Fill each missing
  // unit of an addressed column by the same rule the matrix uses for its
  // defaults — unit 0 as the initial, unit 1 less unit 0 as the delta — and
  // leave a column the adapter never mentions at zero.
  types.forEach(({ categoryKey: key }) => {
    if (!written.has(`${key}\u0000${0}`)) return;
    const initial = matrix[0][key];
    const delta = written.has(`${key}\u0000${1}`) ? matrix[1][key] - initial : 0;
    for (let unitIdx = 0; unitIdx < matrix.length; unitIdx += 1) {
      if (!written.has(`${key}\u0000${unitIdx}`)) matrix[unitIdx][key] = initial + unitIdx * delta;
    }
  });

  return matrix;
}

/**
 * Build the per-column {initial, delta} defaults for a MODBUS adapter
 * from its `register_offsets` map. Emits an entry under EVERY column key
 * the matrix might ask for — both the LN2 categoryKey of each cdo_template
 * read/write entry AND the register-type key the column would render as
 * in byRegisterType mode (coil / discrete_input / holding_register /
 * input_register / write_register). This mirrors the dual-key emit in
 * `deriveModbusBankOffsets` so the matrix's "derived" cell fallback
 * (computeDerivedValue → adapterDefaults2Row[categoryKey]) finds a value
 * regardless of which rendering mode the user has toggled.
 */
function buildModbusAdapterDefaults2Row(
  adapter: { cdo_template?: unknown; cdoTemplate?: unknown; protocol_type?: unknown } | null,
): Record<string, { initial: number; delta: number }> | undefined {
  if (!adapter) return undefined;
  if (resolveAdapterProtocolUpper(adapter) !== "MODBUS") return undefined;

  let cdo: unknown = adapter.cdo_template ?? adapter.cdoTemplate ?? null;
  if (typeof cdo === "string") {
    try {
      cdo = JSON.parse(cdo);
    } catch {
      cdo = null;
    }
  }
  const flat = normalizeModbusRegisterOffsetsForDerive(cdo);
  if (!flat) return undefined;

  const out: Record<string, { initial: number; delta: number }> = {};

  // Emit defaults for every register-type column the matrix can render in
  // byRegisterType mode — adapter readers/writers may not cover every space
  // but the matrix code path still asks adapterDefaults2Row[categoryKey].
  Object.entries(MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS).forEach(([colKey, fields]) => {
    out[colKey] = {
      initial: flat[fields.initialKey] ?? 0,
      delta: flat[fields.deltaKey] ?? 0,
    };
  });

  // Also emit under each LN2 categoryKey discovered in the adapter's
  // reads/writes so the LN2-mode matrix shares the same defaults.
  const categoryKeyToBankCol = buildCategoryKeyToBankColumnMap(cdo);
  Object.entries(categoryKeyToBankCol).forEach(([ln2Key, bankCol]) => {
    const fields = MODBUS_BANK_COLUMN_TO_OFFSET_FIELDS[bankCol];
    if (!fields) return;
    out[ln2Key] = {
      initial: flat[fields.initialKey] ?? 0,
      delta: flat[fields.deltaKey] ?? 0,
    };
  });

  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Derive per-category initial+delta from the adapter's offset_mapping.
 * Returns undefined when the adapter has no offset mapping or the mapping
 * is empty, so that BankOffsetMatrix falls back to legacy behavior. For
 * MODBUS adapters offset_mapping is empty by design — those route through
 * `buildModbusAdapterDefaults2Row` which reads `register_offsets` instead.
 */
function buildAdapterDefaults2Row(
  adapter:
    | {
        offset_mapping?: unknown;
        offsetMappingJson?: unknown;
        cdo_template?: unknown;
        cdoTemplate?: unknown;
        protocol_type?: unknown;
      }
    | null
    | undefined,
  types?: MappedMessageType[],
): Record<string, { initial: number; delta: number }> | undefined {
  if (!adapter) return undefined;
  // MODBUS adapters never populate offset_mapping; derive defaults from
  // register_offsets via the helper above instead.
  if (resolveAdapterProtocolUpper(adapter) === "MODBUS") {
    return buildModbusAdapterDefaults2Row(adapter);
  }
  const rawSource = adapter.offset_mapping ?? adapter.offsetMappingJson;
  // FlatBuffer responses hand back offsetMappingJson as a JSON string; parse it
  // once so the array-shape branch below sees the decoded array.
  const raw =
    typeof rawSource === "string"
      ? (() => {
          try {
            return JSON.parse(rawSource);
          } catch {
            return rawSource;
          }
        })()
      : rawSource;
  const result: Record<string, { initial: number; delta: number }> = {};

  // Preferred path: server now stores offset_mapping as []OffsetMappingEntry
  // (positional array). Use the canonical deserializer to recover grouped
  // pairs and individual bank_level pairs together.
  if (Array.isArray(raw)) {
    // Build column descriptors from types if available so the deserializer can
    // route legacy friendly-name entries to their correct individual columns.
    const columnDescriptors: AdapterColumnDescriptor[] | undefined =
      types && types.length > 0
        ? types.map((t) => ({
            key: t.categoryKey,
            label: t.label,
            isIndividual: Boolean(t.isIndividual),
          }))
        : undefined;
    const parsed = deserializeOffsetMapping(raw, columnDescriptors);
    Object.entries(parsed.ln1UnitOffsets).forEach(([mcStr, pair]) => {
      if (!pair || pair.length < 2) return;
      const mc = Number(mcStr);
      const canonicalKey = MC_TO_KEY[mc];
      if (!canonicalKey) return;
      result[canonicalKey] = { initial: pair[0], delta: pair[1] - pair[0] };
    });
    Object.entries(parsed.ln1IndividualOffsets).forEach(([compositeKey, pair]) => {
      if (!pair) return;
      result[compositeKey] = { initial: pair[0], delta: pair[1] - pair[0] };
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }

  // Legacy fallback: object shape written by older frontend versions.
  const migrated = migrateLegacyOffsetMap(raw);
  Object.entries(migrated).forEach(([key, [a, b]]) => {
    const asNum = Number(key);
    const canonicalKey =
      !Number.isNaN(asNum) && MC_TO_KEY[asNum] !== undefined ? MC_TO_KEY[asNum] : key;
    result[canonicalKey] = { initial: a, delta: b - a };
  });
  return Object.keys(result).length > 0 ? result : undefined;
}

/** Map DevType enum values to the uppercase strings the backend expects. */
const DEV_TYPE_LABELS: Record<DevType, string> = {
  [DevType.Elevator]: LiftType[LiftType.ELEVATOR],
  [DevType.Escalator]: LiftType[LiftType.ESCALATOR],
  [DevType.Bot]: LiftType[LiftType.BOT],
  [DevType.Gpio]: LiftType[LiftType.GPIO],
  [DevType.Relay]: "RELAY",
  [DevType.Diligent]: "DILIGENT",
};

/** Human-readable unit prefix for the offset matrix rows. */
const UNIT_LABEL_BY_TYPE: Partial<Record<DevType, string>> = {
  [DevType.Elevator]: "Car",
  [DevType.Escalator]: "Esc",
  [DevType.Bot]: "Bot",
};

/** Bank-selectable types (subset of DevType). */
const BANK_DEV_TYPES: DevType[] = [DevType.Elevator, DevType.Escalator, DevType.Bot, DevType.Gpio];

/** Types that have floors / stops / rear doors. */
const FLOOR_DEV_TYPES = new Set<DevType>([DevType.Elevator, DevType.Bot]);

/** Resolve a backend lift_type string to a DevType value. */
function devTypeFromString(s: string): DevType {
  const upper = s.toUpperCase();
  const entry = Object.entries(DEV_TYPE_LABELS).find(([, label]) => label === upper);
  return entry ? (Number(entry[0]) as DevType) : DevType.Elevator;
}

/** Backend's seeded name for a lift created without an operator-chosen name
 *  (see `createBankWithLifts` in LiftCloud's bank.controller.go). Not a
 *  real, user-meaningful label — the pin matrix falls through past it. */
const DEFAULT_LIFT_NAME = "default_lift";

/**
 * Resolve a real lift's display label for the pin assignment matrix.
 * Preference order: a non-zero car_num (operator-set, distinct from
 * position/ordering), then the lift's stored name (skipping the backend's
 * "default_lift" seed placeholder), then a synthesized "<Unit> <N>" label
 * derived from its 1-based position.
 */
export function resolveLiftDisplayName(
  lift: { name?: unknown; carNum?: number; position: number },
  devType: DevType,
): string {
  const unit = UNIT_LABEL_BY_TYPE[devType] || "Car";
  if (typeof lift.carNum === "number" && lift.carNum > 0) {
    return `${unit} ${lift.carNum}`;
  }
  const name = typeof lift.name === "string" ? lift.name : "";
  if (name !== "" && name !== DEFAULT_LIFT_NAME) {
    return name;
  }
  return `${unit} ${lift.position + 1}`;
}

/** Wire shape for one entry of `PUT /banks/:bankID/pin-assignments`. */
export interface PinAssignmentPayloadEntry {
  board_address: number;
  pin_num: number;
  lift_id: string;
  bank_id?: string;
}

/**
 * Derive the bank-scoped pin-assignments PUT body from the adapter's full
 * `discrete_2_message` array (the shared pin/lift template spanning every
 * bank the adapter serves — see pinAssignmentCheck.ts). Every entry is
 * included, not just ones touched in this session, since the backend
 * replaces the whole template.
 *
 *  - `lift_id: ""` clears the pin (no lift assigned).
 *  - `bank_id` is included only when the entry belongs to a DIFFERENT bank
 *    than the one being saved — omitted both for this bank's own entries
 *    (the URL already names the bank) and for entries with no bank at all.
 */
export function buildPinAssignmentPayload(
  entries: readonly DiscreteToLn2EntryInterface[],
  bankId: string,
): PinAssignmentPayloadEntry[] {
  return entries.map((entry) => {
    const assignment: PinAssignmentPayloadEntry = {
      board_address: entry.key.board_address,
      pin_num: entry.key.pin_num,
      lift_id: entry.value.lift_id?.id ?? "",
    };
    const entryBankId = entry.value.bank_id?.id;
    if (entryBankId && entryBankId !== bankId) {
      assignment.bank_id = entryBankId;
    }
    return assignment;
  });
}

interface CreatedBank {
  id: string;
  name: string;
  type: string;
  channelName: string;
  channelMode: ChannelMode;
  channelActiveEndpointName: string | null;
  adapterName: string;
  /** Lifts assigned to this bank — the "Total Lifts" column's counter. */
  numberOfLifts: number;
  /**
   * The bank's channel id. Not shown in the table: the "Draw on bank" picker
   * reads the bank's server through it (a bank's server is its channel's).
   */
  channelId: string;
  /** The bank this one draws on through a bank overlay; "" for none. */
  overlayBankId: string;
}

interface ChannelOption {
  id: string;
  name: string;
  mode: ChannelMode;
  /** Authoritative routing flag — drives the routable/adapter mismatch warning. */
  routable: boolean;
  /**
   * The channel's OPC UA endpoint (`""` when it has none). An OPC UA adapter
   * cannot be bound to a channel without one — LiftCloud's
   * `guardOpcUaChannelEndpoint` refuses the save — so this step has to be able
   * to see it rather than let the operator discover it from a server 400.
   */
  opcUaEndpointUrl: string;
  /**
   * The channel's transport and session protocol — drive the warning for a
   * Numato module reached over TCP with no session login. `null` transport:
   * the response named none this step recognises.
   */
  connectionType: ChannelType | null;
  sessionProtocol: SessionProtocol;
}

interface AdapterOption {
  id: string;
  name: string;
  lift_type?: string;
  protocol_type?: string;
  cdo_input?: any;
  cdo_template?: any;
  keys?: any[];
  offset_mapping?: any[];
  intra_content_flags?: any;
}

interface BankFloorDoorRow {
  key: string;
  id: string;
  position: number;
  name: string;
  floorNo: number;
  floorIndex: number;
  floorBelowLobby: boolean;
  frontDoor: boolean;
  rearDoor: boolean;
}

function getDefaultFormState() {
  return {
    name: "",
    devType: DevType.Elevator,
    channel_id: "",
    adapter_id: "",
    number_of_lifts: 1,
    floor_count: 2,
    floor_below_lobby: 0,
    start_floor_no: 1,
    stops: 2,
    front_doors: true,
    rear_doors: false,
    visible: true,
    logical_port: null as number | null,
    event_reporting_address: null as string | null,
    vendor_location_path: null as string | null,
    vendor_site_path: null as string | null,
    vendor_group_path: null as string | null,
    overlay_bank_id: null as string | null,
    overlay_lifts: OverlayLifts.ALL_LIFTS as OverlayLifts,
    stopsLinked: true,
  };
}

/** A saved lift, reduced to what lowering the bank's lift count needs. */
export interface SurplusLift {
  /** Lift id as the DELETE /lifts/:id endpoint wants it. */
  id: string;
  /** The car's position within the bank (1-based, as the grow path assigns it). */
  position: number;
  /** Name, when the loaded row carries one — for the confirmation text. */
  name: string;
}

/**
 * Which already-saved lifts a reduction to `targetCount` has to remove, in the
 * order they should be deleted: highest position first.
 *
 * LiftCloud's `NumberOfLifts` is a free-floating column that never reconciles
 * with the `lifts` rows in either direction — `CreateLift`/`DeleteLift` do not
 * touch it and there is no `reconcileBankLifts` — while CDO generation builds
 * cars from the ROWS. So a bank whose count is lowered without its rows coming
 * with it declares N cars and emits more than N: a silent desync, and strictly
 * worse than not lowering the count at all. Lowering the number is therefore
 * only meaningful as "delete these specific rows too", which is what this
 * picks.
 *
 * Selection is by POSITION, never by array slice index. `d.lifts` is the raw
 * preloaded array from GET /banks/{id}; its order is whatever the association
 * happened to return, and a render-time index into it says nothing about which
 * car is which. Same reasoning as `handleDeleteLift` in StepLiftsFloors.tsx,
 * which identifies its target by object reference for exactly this reason.
 * Ties (missing or duplicated positions) fall back to load order so the
 * most-recently-saved car goes first — the mirror image of the grow path, which
 * appends cars at increasing positions.
 *
 * Returns [] when nothing needs to go, including when the bank has no loaded
 * rows to reason about — a caller must never guess at a deletion it cannot see.
 */
export function selectSurplusLifts(lifts: unknown, targetCount: number): SurplusLift[] {
  if (!Array.isArray(lifts)) return [];

  // Keep order: lowest position first, load order breaking ties. Everything
  // past `targetCount` in that order is surplus.
  const ranked = lifts
    .map((lift, order) => ({
      id: formatUuidT((lift as any)?.id ?? null),
      position: Number((lift as any)?.position ?? 0),
      name: String((lift as any)?.name ?? ""),
      order,
    }))
    .sort((a, b) => a.position - b.position || a.order - b.order);

  if (ranked.length <= targetCount) return [];

  // Reversed so the highest-positioned car is deleted first.
  return ranked
    .slice(targetCount)
    .reverse()
    .map(({ id, position, name }) => ({ id, position, name }));
}

/**
 * One "Created Banks" row from a bank as the by-server list returns it,
 * resolving the channel and adapter names against the lists passed in.
 *
 * Shared by the step's first load and by the reload after a restore, so a
 * restored bank is drawn exactly as it would have been had it never been
 * deleted. A name that cannot be resolved is left blank rather than showing an
 * id.
 */
function toCreatedBankRow(b: any, chList: ChannelOption[], adList: AdapterOption[]): CreatedBank {
  const chId = formatUuidT(b.channelId) || b.channel_id || "";
  const channelObj = chList.find((c) => c.id === chId);
  const adId = formatUuidT(b.adapterId) || b.adapter_id || "";
  return {
    id: formatUuidT(b.id) || b.id,
    name: b.name || b.id,
    type:
      typeof b.type === "number"
        ? (LiftType[b.type as LiftType] ?? LiftType[LiftType.ELEVATOR])
        : b.type || LiftType[LiftType.ELEVATOR],
    channelName: chId ? (channelObj?.name ?? "") : "",
    channelMode: channelObj?.mode ?? ChannelMode.SINGLE,
    channelActiveEndpointName: null,
    adapterName: adId ? (adList.find((ad) => ad.id === adId)?.name ?? "") : "",
    numberOfLifts: b.numberOfLifts ?? b.number_of_lifts ?? 0,
    channelId: chId,
    overlayBankId: readBankOverlay(b).overlayBankId,
  };
}

/**
 * The adapter link a bank record carries, with the adapter's protocol resolved
 * against `adList`. Shared by the load and restore paths, which both start from
 * the server's bank list.
 */
function bankAdapterLink(b: any, adList: AdapterOption[]) {
  const adapterId = formatUuidT(b.adapterId) || b.adapter_id || "";
  return {
    bankId: formatUuidT(b.id) || String(b.id ?? ""),
    adapterId,
    protocol: adapterId
      ? resolveAdapterProtocolUpper(adList.find((ad) => ad.id === adapterId))
      : "",
  };
}

interface StepBanksProps {
  /** When provided, auto-enters edit mode for this bank on mount. */
  editBankId?: string;
  /** Standalone mode hides the wizard's "Created Banks" table. */
  standalone?: boolean;
  /** Called after a successful bank update in standalone mode. */
  onSaveSuccess?: () => void;
  /** Called when the user cancels in standalone mode. */
  onCancel?: () => void;
}

export default function StepBanks({
  editBankId,
  standalone = false,
  onSaveSuccess,
  onCancel,
}: StepBanksProps = {}) {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const {
    state: wizardState,
    setState: setWizardState,
    markStepCompleted,
    unmarkStepCompleted,
    goToStep,
  } = useWizard();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [form, setForm] = useState(getDefaultFormState());
  const [createdBanks, setCreatedBanks] = useState<CreatedBank[]>([]);
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [adapters, setAdapters] = useState<AdapterOption[]>([]);
  const [editingBankId, setEditingBankId] = useState<string | null>(null);
  const access = useCurrentWizardStepAccess();
  const [editingBankData, setEditingBankData] = useState<any | null>(null);
  /**
   * How many lift records this bank already has saved, snapshotted when
   * entering edit mode (handleEditBank). It is the baseline handleUpdateBank
   * diffs form.number_of_lifts against in BOTH directions: above it the
   * missing cars are auto-created, below it `selectSurplusLifts` names the
   * rows that have to be deleted before the smaller count is written.
   * Reset to 0 whenever editing ends (cancelEdit, handleUpdateBank's own
   * post-save reset) so a subsequent create doesn't inherit a stale baseline.
   */
  const [existingLiftCount, setExistingLiftCount] = useState(0);
  const [bankFloorDoorRows, setBankFloorDoorRows] = useState<BankFloorDoorRow[]>([]);
  // Display-only ordering for the Bank Floors table: sorted by `position`,
  // ascending by default. Toggled via the invert-sort button in the table
  // header. This never mutates `bankFloorDoorRows` itself — it's purely a
  // derived view so edits keep writing to the correct row via `record.key`.
  const [bankFloorSortDesc, setBankFloorSortDesc] = useState(false);
  const [manualBankFloorEditingEnabled, setManualBankFloorEditingEnabled] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  // Snapshot of the bank's editable fields when edit mode opened. Used by
  // handleUpdateBank to diff against the post-save state and only unmark
  // wizard steps that genuinely depend on the changed fields. Without
  // this, every bank update — even a name typo correction — forced
  // Lifts/Debounce/BACnet to re-walk regardless of what changed.
  // See utils/wizardInvalidation.ts for the rule table.
  const originalBankSnapshotRef = useRef<Record<string, unknown> | null>(null);

  // Offset matrix state
  const [mappedTypes, setMappedTypes] = useState<MappedMessageType[]>([]);
  const [liftOffsets, setLiftOffsets] = useState<Record<string, number>[]>([]);
  const [intraContent, setIntraContent] = useState(false);
  // The RAW per-MessageContent flags, kept beside the any-true rollup above.
  // The rollup is still used elsewhere; the matrix needs the real flags so one
  // flagged category cannot suppress every other category's overlaps.
  const [intraContentFlags, setIntraContentFlags] = useState<
    Partial<Record<number | string, boolean[]>> | undefined
  >(undefined);
  const [adapterKeyInfos, setAdapterKeyInfos] = useState<AdapterKeyInfo[]>([]);
  // Carries only the direction of each key, for the bank matrix's overlap
  // detection. Kept separate from adapterKeyInfos because that is deliberately
  // EMPTY for LN1-family adapters, and direction is exactly what the
  // range-based fallback needs to stop flagging a legal opposite-direction pair.
  const [ln1KeyMappingsForMatrix, setLn1KeyMappingsForMatrix] = useState<Ln1KeyMappingRow[]>([]);
  const [disabledColumns, setDisabledColumns] = useState<Set<string>>(new Set());
  const [bankHasOverlaps, setBankHasOverlaps] = useState(false);
  const [adapterDefaultOffsets, setAdapterDefaultOffsets] = useState<Record<string, number>[]>([]);
  // Keep the full adapter view loaded for the current bank edit so
  // `buildAdapterDefaults2Row` has the offset_mapping string that the lighter
  // list-adapter entries omit. The list endpoint `GET /adapters?page=1` returns
  // summaries without offset_mapping; we populate this from the dedicated
  // detail fetch in the bank-load flow.
  const [currentBankAdapter, setCurrentBankAdapter] = useState<{
    offset_mapping?: unknown;
    offsetMappingJson?: unknown;
  } | null>(null);
  const [initialSimpleBaseOffsets, setInitialSimpleBaseOffsets] = useState<number[] | null>(null);
  const [registerTypeMappedTypes, setRegisterTypeMappedTypes] = useState<MappedMessageType[]>([]);
  const [categoryToRegType, setCategoryToRegType] = useState<Record<string, string>>({});

  /**
   * Push one derived set of matrix inputs into the seven pieces of state the
   * offset matrix reads, and hand back the columns.
   *
   * Seven setters, called together, from one value. Both adapter-load paths go
   * through here for the same reason they both go through
   * `deriveAdapterMatrixInputs`: the bug this replaces was one path calling six
   * of these seven and leaving `setLn1KeyMappingsForMatrix` out, which is
   * indistinguishable at the matrix from an adapter that genuinely has no keys —
   * so the matrix silently gave up direction-based excuses and real column
   * spans, and disagreed with the adapter page about the same keys.
   */
  const applyAdapterMatrixInputs = (inputs: AdapterMatrixInputs): MappedMessageType[] => {
    setIntraContent(inputs.intraContent);
    setIntraContentFlags(inputs.intraContentFlags);
    setMappedTypes(inputs.mappedTypes);
    setAdapterKeyInfos(inputs.adapterKeys);
    setLn1KeyMappingsForMatrix(inputs.ln1KeyMappings);
    setRegisterTypeMappedTypes(inputs.registerTypeMappedTypes);
    setCategoryToRegType(inputs.categoryToRegType);
    return inputs.mappedTypes;
  };

  // Composite key of the selected channel's heartbeat message, resolved from a
  // single-channel fetch (the by-server list omits a reliably-decodable
  // heartbeat_message). Drives the defensive heartbeat-mapping warning below:
  // a heartbeat can only fire if the paired adapter actually maps that message.
  const [selectedChannelHeartbeatKey, setSelectedChannelHeartbeatKey] = useState<string | null>(
    null,
  );

  // Pin assignment state (Sequent / Numato adapters)
  const [pinEntries, setPinEntries] = useState<DiscreteToLn2EntryInterface[]>([]);
  const [pinAdapterId, setPinAdapterId] = useState<string>("");
  const [isPinProto, setIsPinProto] = useState(false);
  // The bank's REAL lifts (LiftCloud-assigned UUIDs), fetched fresh whenever
  // a pin-mapped bank is opened for editing. The pin matrix must never be fed
  // synthesized index ids ("0", "1", ...) — the packer can't parse those as a
  // UUID and CDO generation silently drops the pin. Sorted by position.
  const [bankLifts, setBankLifts] = useState<PinAssignmentLift[]>([]);

  // Node -> lift matrix state (OPC UA adapters). Bank-scoped: the adapter's
  // Mapping is bank-agnostic, so which node belongs to which lift is
  // authored per bank and stored on the Bank, not the adapter.
  const [isOpcUaProto, setIsOpcUaProto] = useState(false);
  const [isMceProto, setIsMceProto] = useState(false);
  // OPC DA and OPC UA both address points by a vendor node path whose upper
  // segments vary per deployment. The adapter holds only the leaf names, so
  // those segments are entered here.
  const [isOpcPathProto, setIsOpcPathProto] = useState(false);
  const [opcUaAdapterMapping, setOpcUaAdapterMapping] = useState<AdapterCdoOpcUa | null>(null);
  const [opcUaNodeLiftMapping, setOpcUaNodeLiftMapping] = useState<Record<string, string>>({});

  // ************************************************ */
  // DRAFT PERSISTENCE ****************************** */
  // The bank form is the wizard's most complex per-step form: identity
  // fields (name, devType, channel/adapter binding), per-bank geometry
  // (number of lifts, floor count), an offset matrix the operator
  // builds row by row, and pin assignments for Sequent / Numato
  // adapters. The created bank list itself is server-side (the wizard
  // re-hydrates `createdBanks` from `wizardState.bankIds`), but the
  // currently-being-edited form sits entirely in React state until
  // "Save" or "Update" — until then a reload wipes it.
  //
  // We persist exactly the operator-decided slice. Derived state
  // (mappedTypes, adapterKeyInfos, disabledColumns, etc.) is
  // recomputed from the form's adapter/channel binding on restore.
  //
  // Verification: standalone mode (the edge-management editor) and
  // edit-mode-from-existing-bank are excluded — both have their own
  // hydration paths and would race the draft restore.
  useWizardStepDraft<{
    form: ReturnType<typeof getDefaultFormState>;
    editingBankId: string | null;
    liftOffsets: Record<string, number>[];
    intraContent: boolean;
    pinEntries: DiscreteToLn2EntryInterface[];
  }>({
    orgId: wizardState.organizationId,
    step: WizardStep.Banks,
    scopeId: "banks-form",
    scopeType: "step",
    draft: { form, editingBankId, liftOffsets, intraContent, pinEntries },
    onRestore: (saved) => {
      if (saved.form && typeof saved.form === "object") {
        // Spread defensively over the default state so any new field
        // added to BankFormState after this draft was written gets a
        // valid default rather than `undefined`.
        setForm({ ...getDefaultFormState(), ...saved.form });
      }
      if (typeof saved.editingBankId === "string") setEditingBankId(saved.editingBankId);
      if (Array.isArray(saved.liftOffsets)) setLiftOffsets(saved.liftOffsets);
      if (typeof saved.intraContent === "boolean") setIntraContent(saved.intraContent);
      if (Array.isArray(saved.pinEntries)) setPinEntries(saved.pinEntries);
    },
    // Standalone editor mode and "editing an already-saved bank" both
    // hydrate from the server; the wizard draft would clobber that
    // hydration with stale operator-typed values from a previous
    // session. Restrict the draft to fresh-create flow.
    disabled: standalone || !!editBankId,
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostBank,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
      reset: resetPost,
    },
  ] = usePostBankMutation();

  const [triggerGetChannels, { isLoading: isLoadingChannels }] = useLazyGetChannelsByServerQuery();
  const [triggerGetChannel] = useLazyGetChannelQuery();

  const [triggerGetAdapters, { isLoading: isLoadingAdapters }] = useLazyGetAdaptersQuery();
  const [triggerGetAdapter] = useLazyGetAdapterQuery();
  const [triggerGetAdapterKeys] = useLazyGetAdapterKeysQuery();
  const [triggerGetLiftsByBank] = useLazyGetLiftsByBankQuery();
  const [triggerPostLift] = usePostLiftMutation();
  const [triggerDeleteLift] = useDeleteLiftMutation();

  const [triggerGetBank] = useLazyGetBankQuery();
  const [triggerGetBanksByServer] = useLazyGetBanksByServerQuery();
  const [triggerPutBank, { isLoading: isLoadingPut }] = usePutBankMutation();
  const [triggerPutBankPinAssignments] = usePutBankPinAssignmentsMutation();

  const [triggerDeleteBank, { isLoading: isLoadingDelete }] = useDeleteBankMutation();
  const [triggerDeleteBankFloor] = useDeleteBankFloorMutation();
  // Id of the floor currently being deleted, so its row shows a spinner and
  // every delete button disables — the cascade resequences the other rows, so
  // a second concurrent delete would act on stale positions.
  const [deletingFloorId, setDeletingFloorId] = useState<string | null>(null);

  // Resolve the selected channel's heartbeat message whenever the channel
  // selection changes. The heartbeat message has no adapter relationship at
  // channel-creation time, so this pairing is the first place we can validate
  // it against a concrete adapter (see renderHeartbeatMappingWarning).
  useEffect(() => {
    let cancelled = false;
    const chId = form.channel_id;
    if (!chId) {
      setSelectedChannelHeartbeatKey(null);
      return undefined;
    }
    (async () => {
      try {
        const token = await GETJwtToken();
        const result = await triggerGetChannel({ channelId: chId, token }).unwrap();
        const d: any = result?.data || result;
        const key = d?.heartbeatMessage ? messageTToCompositeKey(d.heartbeatMessage) : null;
        if (!cancelled) setSelectedChannelHeartbeatKey(key ?? null);
      } catch {
        if (!cancelled) setSelectedChannelHeartbeatKey(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.channel_id]);

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const onChangeFormValue = (newValue: string, variableName: string) => {
    setForm((prev) => ({ ...prev, [variableName]: newValue }));
  };

  const typeLabel = DEV_TYPE_LABELS[form.devType];

  // LIFT-393: a pin-mapped adapter (Sequent/Numato) whose pins are all assigned
  // to other banks — or to no bank — produces a CDO LiftCloud refuses to build
  // for this bank. Only enforced while editing an existing bank, since the pin
  // matrix is not rendered during creation and there is nothing to assign yet.
  const pinAssignmentBlocksSave = () =>
    isPinProto && Boolean(editingBankId) && bankHasNoAssignedPins(pinEntries, editingBankId ?? "");

  // The channel/adapter pairing LiftCloud's `guardOpcUaChannelEndpoint` refuses
  // outright: an OPC UA adapter bound to a channel with no endpoint URL. Both
  // halves are read from the same two selects `renderRoutableMismatchWarning`
  // below consults, and the protocol is normalised by the same
  // `resolveAdapterProtocolUpper` the bank-load path uses to decide it is OPC
  // UA, so the gate and the node->lift matrix cannot disagree about it.
  // Returns the offending channel's name so the alert can name where to fix it;
  // null when the pairing is fine or too incompletely chosen to judge.
  const opcUaEndpointBlocksSave = (): string | null => {
    const selCh = channels.find((c) => c.id === form.channel_id);
    const selAd = adapters.find((a) => a.id === form.adapter_id);
    if (!selCh || !selAd) return null;
    if (
      !opcUaChannelEndpointMissing({
        adapterProtocol: resolveAdapterProtocolUpper(selAd),
        channelOpcUaEndpointUrl: selCh.opcUaEndpointUrl,
      })
    ) {
      return null;
    }
    return selCh.name || "the selected channel";
  };

  // The protocol of the adapter the form currently names, resolved by the same
  // helper the rest of this step uses. "" until an adapter is picked, in which
  // case only the schema's int16 cap applies to the Logical Port.
  const assignedAdapterProtocol = useMemo(
    () => resolveAdapterProtocolUpper(adapters.find((a) => a.id === form.adapter_id)),
    [adapters, form.adapter_id],
  );

  // A Logical Port the assigned adapter's protocol cannot carry. LobbyVision2
  // stamps it into a uint8 bank number and LiftCloud answers anything outside
  // 0..255 with a 400; every protocol is capped by the schema's int16, which the
  // FlatBuffer serializer would otherwise wrap silently. Blocks the save so the
  // operator learns it here instead of from the server.
  const logicalPortInvalid = useMemo(
    () => logicalPortOutOfRange(assignedAdapterProtocol, form.logical_port),
    [assignedAdapterProtocol, form.logical_port],
  );

  // The three OPC path segments as the bank was loaded, for the partial-state
  // rule: `null` for a bank being created (and while no loaded bank backs the
  // form), so a new bank can never start out partial. Derived from the loaded
  // record rather than kept in a second piece of state, so it cannot outlive the
  // edit it belongs to.
  const initialOpcVendorPaths = useMemo(
    () => (editingBankId && editingBankData ? readOpcVendorPathValues(editingBankData) : null),
    [editingBankId, editingBankData],
  );

  // Only an OPC-addressed bank has the inputs, so only it can be partial.
  const opcVendorPathVerdict = isOpcPathProto
    ? opcVendorPathTransition(initialOpcVendorPaths, form)
    : "ok";

  // The node path the three inputs will compose, with the first loaded lift's
  // own segment when there is one. A bank being created has no lift yet, so its
  // preview shows the bank prefix and the leaf alone.
  const opcVendorPathPreview = isOpcPathProto
    ? composeOpcUaNodePreview(
        form,
        editingBankId ? firstLiftVendorPath(editingBankData?.lifts) : null,
      )
    : null;

  const isFormValid = () =>
    form.name.trim() !== "" &&
    form.channel_id !== "" &&
    form.adapter_id !== "" &&
    !logicalPortInvalid &&
    !pinAssignmentBlocksSave() &&
    opcUaEndpointBlocksSave() === null &&
    opcVendorPathVerdict !== "block";

  // LIFT-393 guidance for pin-mapped adapters. Two distinct states, mirroring
  // how LiftCloud's CDO generation treats each: no pins assigned to this bank is
  // an error that blocks the save (the CDO build refuses it), while pins left
  // assigned to no bank at all are dropped from every CDO with a warning and so
  // only warrant a notice here. Decision logic lives in pinAssignmentCheck so it
  // is unit-tested.
  const renderPinAssignmentAlerts = () => {
    const unassigned = countUnassignedPins(pinEntries);
    const blocked = pinAssignmentBlocksSave();
    // Retrofit case: pins naming a car this bank does not have. Legacy CDOs
    // carry a synthesized index ("0", "1") where a lift UUID belongs, and a pin
    // can outlive the car it points at. Both read as assigned until the id is
    // checked against the bank's real lifts, and LiftCloud drops them.
    const unresolvable = countUnresolvableLiftPins(pinEntries, editingBankId ?? "", bankLifts);
    if (!blocked && unassigned === 0 && unresolvable === 0) return null;
    return (
      <div className="row">
        {blocked && (
          <div className="col-12 mb-3">
            <Alert
              type="error"
              showIcon
              message={`No pins are assigned to "${form.name || "this bank"}". Assign at least one pin to a car below — LiftCloud cannot build a CDO for a bank with no assigned pins, and the edge will receive no traffic for it.`}
            />
          </div>
        )}
        {unresolvable > 0 && (
          <div className="col-12 mb-3">
            <Alert
              type="warning"
              showIcon
              message={`${unresolvable} pin${unresolvable === 1 ? "" : "s"} on this bank name${unresolvable === 1 ? "s" : ""} a car that no longer exists`}
              description={`Marked "Unknown car" below. LiftCloud cannot resolve the id, so ${unresolvable === 1 ? "that pin is" : "those pins are"} dropped from the generated CDO and no controller traffic on ${unresolvable === 1 ? "it" : "them"} reaches the platform. Reassign ${unresolvable === 1 ? "it" : "them"} to one of this bank's cars. Saving is still allowed so the rest of your changes are not held up.`}
            />
          </div>
        )}
        {unassigned > 0 && (
          <div className="col-12 mb-3">
            <Alert
              type="warning"
              showIcon
              message={`${unassigned} pin${unassigned === 1 ? " is" : "s are"} not assigned to any bank or car. ${unassigned === 1 ? "It" : "They"} will be omitted from every generated CDO, so no controller traffic on ${unassigned === 1 ? "that pin" : "those pins"} reaches the platform.`}
            />
          </div>
        )}
      </div>
    );
  };

  // Non-blocking routing-mismatch guidance for the currently-selected
  // channel + adapter pairing. Both cases are ALLOWED (VTS handles each) — this
  // only warns the operator that runtime behavior may differ from intent.
  // Decision logic lives in constants/channelRouting so it is unit-tested.
  const renderRoutableMismatchWarning = () => {
    const selCh = channels.find((c) => c.id === form.channel_id);
    const selAd = adapters.find((a) => a.id === form.adapter_id);
    if (!selCh || !selAd) return null;
    const proto = resolveAdapterProtocolUpper(selAd);
    const kind = routableMismatchWarning(selCh.routable === true, proto);
    if (!kind) return null;
    const chName = selCh.name || "channel";
    if (kind === "NON_ROUTABLE_ON_ROUTED_ADAPTER") {
      return (
        <div className="row">
          <div className="col-12 mt-3">
            <Alert
              type="warning"
              showIcon
              message={`Adapter protocol ${proto} is always routed (CGA / LobbyVision / LobbyVision2), but channel "${chName}" is Single (not routable). VTS force-routes CGA/LobbyVision and falls back to the direct path for LobbyVision2 — set the channel's type to Routable for correct multi-bank routing.`}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="row">
        <div className="col-12 mt-3">
          <Alert
            type="warning"
            showIcon
            message={`Channel "${chName}" is Routable, but adapter protocol ${proto} has no routed dispatch in VTS — the direct vendor path is used and routing is ignored. This is allowed.`}
          />
        </div>
      </div>
    );
  };

  // Non-blocking guidance for a Numato adapter whose module the selected channel
  // reaches over TCP with no session login: VTS then logs in with the CDO's
  // placeholder (user / password), which a real module refuses unless those are
  // its credentials. Decision logic lives in constants/numatoSession, unit-tested.
  const renderNumatoSessionWarning = () => {
    const selCh = channels.find((c) => c.id === form.channel_id);
    const selAd = adapters.find((a) => a.id === form.adapter_id);
    if (!selCh || !selAd) return null;
    const proto = resolveAdapterProtocolUpper(selAd);
    if (!numatoNeedsSession(proto, selCh)) return null;
    return (
      <div className="row">
        <div className="col-12 mt-3">
          <Alert
            type="warning"
            showIcon
            data-testid="bank-numato-session-warning"
            message={`Channel "${selCh.name || "channel"}" reaches this ${proto} module over TCP with no session protocol, so VTS logs in with the placeholder login (user / password), which the module refuses unless those are its credentials. Give the channel a Telnet session with the module's user name and password in the Channels step.`}
          />
        </div>
      </div>
    );
  };

  // Manual floor editing is gated behind a confirmation because each of the three
  // editable columns reaches somewhere the operator cannot see from this screen,
  // and two of them reach backwards into data already recorded:
  //
  //   Floor Index — the key historical events are matched on. Reports build a
  //     floor_index -> name map (LiftCloud reporting.CreateFloorsMap) and resolve
  //     every stored event position through it at query time. Change this and past
  //     faults, trips and service events move between floor labels.
  //   Name        — relabels this floor everywhere it has ever appeared, including
  //     on report rows recorded before the change.
  //   Position    — feeds the bank's floor ordering, per-lift stops and the
  //     generated CDO, so it changes which physical floor the controller is told to
  //     address.
  //
  // The list is spelled out rather than summarised as "may alter the bank
  // configuration": the previous wording named no consequence, so an operator had
  // no way to tell that editing Floor Index rewrites the past.
  const renderManualFloorEditingWarning = () => (
    <div>
      <p>{t("general.enableManualEditingWarning")}</p>
      <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
        <li>{t("general.enableManualEditingWarningFloorIndex")}</li>
        <li>{t("general.enableManualEditingWarningName")}</li>
        <li>{t("general.enableManualEditingWarningPosition")}</li>
      </ul>
    </div>
  );

  // Floor deletion is a server-side cascade, not a local row removal: the floor
  // and its per-lift stops are soft-deleted, the surviving floors' Position is
  // resequenced and the bank's floor_count is decremented, all in one
  // transaction. Local rows are therefore discarded and reloaded from the server
  // afterwards rather than patched in place — the client cannot predict the
  // reconciled result, and guessing would leave the editor disagreeing with the
  // rows a save would then write back.
  const handleDeleteBankFloor = (record: BankFloorDoorRow) => {
    if (!editingBankId || bankFloorDoorRows.length <= 1) return;
    Modal.confirm({
      title: t("general.deleteFloorTitle", { name: record.name || record.floorIndex }),
      content: (
        <div>
          <p>{t("general.deleteFloorWarning")}</p>
          <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
            <li>{t("general.deleteFloorWarningStops")}</li>
            <li>{t("general.deleteFloorWarningReports")}</li>
            <li>{t("general.deleteFloorWarningIndex")}</li>
          </ul>
        </div>
      ),
      width: 560,
      okText: t("general.delete"),
      okButtonProps: { danger: true },
      cancelText: t("general.cancel"),
      onOk: async () => {
        setDeletingFloorId(record.id);
        try {
          const token = await GETJwtToken();
          await triggerDeleteBankFloor({
            bankId: editingBankId,
            floorId: record.id,
            token,
          }).unwrap();
          // Deliberately narrower than handleDeleteBank's range: the bank
          // survives — the guard at the top of this handler refuses to
          // delete the last floor — so only the floor roster changed. That
          // is exactly the change BANK_FIELD_RULES.floorCount already
          // describes on the UPDATE path, and it maps to Lifts alone.
          // Placed before the refetch so a failed reload cannot swallow an
          // invalidation the completed DELETE has already earned.
          unmarkStepCompleted(WizardStep.Lifts);
          // Force a fresh GET after floor deletion so the reconciled floor list
          // is reflected immediately instead of waiting for a full page reload.
          await handleEditBank(editingBankId, true);
        } catch {
          message.error(t("general.deleteFloorFailed"));
        } finally {
          setDeletingFloorId(null);
        }
      },
    });
  };

  const handleManualBankFloorEditingToggle = (checked: boolean) => {
    if (!checked) {
      setManualBankFloorEditingEnabled(false);
      return;
    }
    Modal.confirm({
      title: t("general.enableManualEditingTitle"),
      content: renderManualFloorEditingWarning(),
      width: 560,
      okText: t("general.enable"),
      cancelText: t("general.cancel"),
      onOk: () => setManualBankFloorEditingEnabled(true),
    });
  };

  // Defensive check: a channel's heartbeat message must be one the paired
  // adapter actually maps, otherwise the heartbeat will never arrive and the
  // channel is flagged offline at runtime. We can only enforce this once a
  // channel and an adapter are associated on a bank — at channel-creation time
  // there is no adapter to filter against. Compared at message-category
  // granularity (the adapter's mapped types are category-level). Non-blocking,
  // and skipped entirely when the adapter's messages aren't enumerable
  // (mappedTypes empty) to avoid false positives on opaque protocols.
  const renderHeartbeatMappingWarning = () => {
    if (!form.adapter_id) return null;
    const adapterCategoryKeys = [
      ...mappedTypes.map((mt) => mt.categoryKey),
      ...adapterKeyInfos.map((k) => k.categoryKey),
    ];
    const unmapped = unmappedHeartbeatCategory(selectedChannelHeartbeatKey, adapterCategoryKeys);
    if (!unmapped) return null;
    const selCh = channels.find((c) => c.id === form.channel_id);
    const selAd = adapters.find((a) => a.id === form.adapter_id);
    const chName = selCh?.name || "channel";
    const adName = selAd?.name || "adapter";
    const hbLabel = getDisplayName(selectedChannelHeartbeatKey ?? "");
    return (
      <div className="row">
        <div className="col-12 mt-3">
          <Alert
            type="warning"
            showIcon
            message={`Channel "${chName}" uses heartbeat message "${hbLabel}", but adapter "${adName}" does not map that message. The heartbeat will never arrive and the channel may be reported offline — map this message on the adapter, or change the channel's heartbeat message.`}
          />
        </div>
      </div>
    );
  };

  // Blocking guidance for the OPC UA pairing `opcUaEndpointBlocksSave` detects.
  // An error rather than a warning, and deliberately not the server's text:
  // LiftCloud answers this pairing with a bare 400 that names neither the
  // channel nor the field, which is what made the failure unavoidable in the
  // field. This says which channel to fix, what to set on it, and where.
  const renderOpcUaEndpointAlert = () => {
    const blockedChannel = opcUaEndpointBlocksSave();
    if (!blockedChannel) return null;
    return (
      <div className="row">
        <div className="col-12 mt-3">
          <Alert
            type="error"
            showIcon
            message={`Channel "${blockedChannel}" has no OPC UA Endpoint URL — this bank cannot be saved`}
            description={`An OPC UA adapter reaches its server through the endpoint URL stored on the channel it is bound to, and "${blockedChannel}" has none. Open the Channels step, edit "${blockedChannel}", and set its OPC UA Endpoint URL to the server address in the form opc.tcp://host:port (for example opc.tcp://10.0.0.5:4840). "${
              editingBankId ? "Save" : "+ Add Bank"
            }" unlocks as soon as the channel has one.`}
          />
        </div>
      </div>
    );
  };

  // Blocking guidance for the Logical Port `logicalPortInvalid` detects: says
  // which range applies and why the save is locked, rather than leaving a
  // disabled button to explain nothing.
  const logicalPortRangeMessage = (): string => {
    const min = logicalPortMin(assignedAdapterProtocol);
    const max = logicalPortMax(assignedAdapterProtocol);
    const forWhom = assignedAdapterProtocol
      ? `a ${assignedAdapterProtocol} adapter`
      : "a bank on any adapter";
    return `Logical Port ${form.logical_port} is outside the range ${forWhom} accepts. Enter a whole number from ${min} to ${max} — "${
      editingBankId ? "Update Bank" : "+ Add Bank"
    }" unlocks as soon as it is in range.`;
  };

  const fetchChannels = async (): Promise<ChannelOption[]> => {
    if (!wizardState.serverId) return [];
    const token = await GETJwtToken();
    const result = await triggerGetChannels({
      serverId: wizardState.serverId,
      page: 1,
      limit: 100,
      token,
      searchAndFilterString: "",
    });
    if (result.data && result.data.data) {
      const channelList = result.data.data.map((ch: any) => {
        const chId = formatUuidT(ch.id) || String(ch.id ?? "");
        return {
          id: chId,
          name: String(ch.name || chId),
          mode: (ch.mode as ChannelMode) ?? ChannelMode.SINGLE,
          routable: ch.routable === true,
          // Read both spellings: the FlatBuffer response names this camelCase,
          // a JSON one snake_case, and either can arrive as raw UTF-8 bytes.
          // Same tolerant read StepChannels' edit path uses.
          opcUaEndpointUrl:
            coerceFlatBufferText(ch.opcUaEndpointUrl) ??
            coerceFlatBufferText(ch.opc_ua_endpoint_url) ??
            "",
          connectionType: channelTypeOf(ch.connectionType ?? ch.connection_type),
          // A channel with no session carries no `session` table, which reads
          // as protocol None.
          sessionProtocol: sessionConfigFrom(ch.session).protocol,
        };
      });
      setChannels(channelList);
      return channelList;
    }
    return [];
  };

  const fetchAdapters = async (): Promise<AdapterOption[]> => {
    if (!wizardState.serverId) return [];
    const token = await GETJwtToken();
    const result = await triggerGetAdapters({
      page: 1,
      limit: 100,
      token,
      // Adapters are org-global (no server_id column); fetch all.
      searchAndFilterString: "",
    });
    if (result.data && result.data.data) {
      // Adapters are org-global, so the list endpoint returns every adapter
      // in the org. Scope down to the ones referenced/added on the wizard's
      // Adapters step (the only authoritative org<->adapter link — see
      // mergeAddedAdapterIds.ts) before transforming, so the shared `adapters`
      // state (dropdown, onChange lookups, hydrate-time name resolution)
      // never sees adapters outside this wizard session.
      const referencedIds = new Set(wizardState.adapterIds);
      const adapterList = result.data.data
        .filter((ad: any) => referencedIds.has(formatUuidT(ad.id) || String(ad.id ?? "")))
        .map((ad: any) => {
          const adId = formatUuidT(ad.id) || String(ad.id ?? "");
          const proto =
            ad.protocolType != null
              ? (AdapterProtocol[ad.protocolType as AdapterProtocol] ?? String(ad.protocolType))
              : ad.protocol_type || "";
          const lt =
            ad.liftType != null
              ? (LiftType[ad.liftType as LiftType] ?? String(ad.liftType))
              : ad.lift_type || "";
          return {
            id: adId,
            name: String(ad.name || adId),
            lift_type: lt,
            protocol_type: proto,
            cdo_input: ad.cdoInputJson ?? ad.cdo_input,
            cdo_template: ad.cdoTemplateJson ?? ad.cdo_template,
            keys: ad.keys,
            offset_mapping: ad.offsetMappingJson ?? ad.offset_mapping,
            intra_content_flags: ad.intraContentFlagsJson ?? ad.intra_content_flags,
          };
        });
      setAdapters(adapterList);
      return adapterList;
    }
    return [];
  };

  const handleAddBank = async () => {
    if (!isFormValid()) return;
    if (logicalPortInvalid) {
      setStepError(logicalPortRangeMessage());
      return;
    }
    resetPost();
    const token = await GETJwtToken();
    const body: Record<string, unknown> = {
      id: crypto.randomUUID(),
      name: form.name,
      type: typeLabel,
      channel_id: form.channel_id,
      adapter_id: form.adapter_id,
      org_id: wizardState.organizationId,
      number_of_lifts: form.number_of_lifts,
      floor_count: form.floor_count,
      floor_below_lobby: form.floor_below_lobby,
      start_floor_no: form.start_floor_no,
      stops: form.stops,
      front_doors: form.front_doors,
      rear_doors: form.rear_doors,
      visible: form.visible,
      active: true,
      interface_json: null,
    };
    if (form.logical_port != null) {
      body.logical_port = form.logical_port;
    }
    // For an OPC-addressed bank the wizard is the authoritative editor of the
    // three vendor path segments: it re-sends all three on every save (empty
    // string for a cleared input) and flags the set as provided, so the server
    // writes each to its value or clears it to NULL. Any other protocol does not
    // show these fields, so the payload omits them and the server's guarded
    // write leaves any stored columns untouched.
    if (isOpcPathProto) {
      OPC_VENDOR_PATH_FIELDS.forEach((field: OpcVendorPathField) => {
        const value = form[field];
        body[field] = value != null ? value.trim() : "";
      });
      body.vendor_paths_provided = true;
    }
    // The "Draw on bank" controls render for every bank, so this step is the
    // authoritative editor of the overlay on every save: the flag always goes,
    // and the target with it when one is chosen.
    Object.assign(body, overlaySaveFields(form.overlay_bank_id, form.overlay_lifts));

    if (form.event_reporting_address != null && form.event_reporting_address.trim() !== "") {
      body.event_reporting_address = form.event_reporting_address.trim();
    }
    if (liftOffsets.length > 0) {
      body.offset_mapping_json = JSON.stringify(
        liftOffsetsToKeyedMaps(liftOffsets, mappedTypes, categoryToRegType),
      );
    }
    // Sent unconditionally (unlike offset_mapping_json above): disabledColumns
    // is independent local state — the offset matrix's column-toggle checkbox
    // updates it without ever touching liftOffsets, so a user can disable a
    // column before entering any offset value. Gating this on
    // liftOffsets.length > 0 would silently drop that choice.
    body.disabled_columns_json = JSON.stringify(Array.from(disabledColumns));
    if (isOpcUaProto && Object.keys(opcUaNodeLiftMapping).length > 0) {
      body.opc_ua_node_lift_mapping_binary = opcUaNodeLiftIndexBindingsToBase64(
        buildOpcUaNodeLiftIndexBindingsBytes(nodeLiftMapToValidEntries(opcUaNodeLiftMapping)),
      );
    }
    triggerPostBank({ body, token });
  };

  const getChannelNameById = (id: string): string => {
    const found = channels.find((ch) => ch.id === id);
    return found ? found.name : id;
  };

  const getChannelModeById = (id: string): ChannelMode => {
    const found = channels.find((ch) => ch.id === id);
    return found?.mode ?? ChannelMode.SINGLE;
  };

  const getAdapterNameById = (id: string): string => {
    const found = adapters.find((ad) => ad.id === id);
    return found ? found.name : id;
  };

  const resetOffsetEditorState = () => {
    setMappedTypes([]);
    setLiftOffsets([]);
    setIntraContent(false);
    setIntraContentFlags(undefined);
    setAdapterKeyInfos([]);
    setLn1KeyMappingsForMatrix([]);
    setDisabledColumns(new Set());
    setBankHasOverlaps(false);
    setAdapterDefaultOffsets([]);
    setCurrentBankAdapter(null);
    setInitialSimpleBaseOffsets(null);
    setPinEntries([]);
    setPinAdapterId("");
    setIsPinProto(false);
    setBankLifts([]);
    setIsOpcUaProto(false);
    setIsOpcPathProto(false);
    setOpcUaAdapterMapping(null);
    setOpcUaNodeLiftMapping({});
    setManualBankFloorEditingEnabled(false);
  };

  const handleDeleteBank = async (bankId: string) => {
    const token = await GETJwtToken();
    try {
      await triggerDeleteBank({ id: bankId, token }).unwrap();
      setCreatedBanks((prev) => prev.filter((b) => b.id !== bankId));
      setWizardState((prev) => {
        const updated = structuredClone(prev);
        updated.bankIds = updated.bankIds.filter((id: string) => id !== bankId);
        delete updated.bankNames[bankId];
        delete updated.bankTypes[bankId];
        delete updated.adapterIdByBank[bankId];
        delete updated.adapterProtocolByBank[bankId];
        if (updated.liftIdsByBank) {
          delete updated.liftIdsByBank[bankId];
        }
        return updated;
      });
      // Bank deletion dangles every reference to the bank from Channel
      // Modifiers onward: Lifts, Debounce and BACnet key off it directly,
      // and Notifications and Actions off its per-message routing. Users
      // is included too — role scope grants are per-bank and an empty
      // grant map means allow-all rather than deny-all (see
      // redux/scope/scopeSlice.ts), so a role scoped only to this bank
      // silently stops being restricted. MonitorGrid and Review have no
      // bank-specific content of their own to invalidate, but are included
      // anyway: SetupWizardPage's forward-jump guard
      // (`step <= currentStep || isStepCompleted(step) ||
      // isStepCompleted(step - 1)`) is a single immediately-prior check,
      // not a sequential walk, so any step left complete inside the range
      // — MonitorGrid included — becomes a one-click hop-in target that
      // skips past Notifications/Actions from anywhere, and a
      // stale-complete Review shows a false "finish" and lets the
      // operator jump past everything this delete just invalidated. The
      // literal this replaces began as `(5, 6, 7)` under the
      // pre-ChannelModifiers numbering and drifted when index 5 was
      // inserted; a stated range cannot drift again.
      unmarkStepCompleted(...wizardStepsFrom(WizardStep.ChannelModifiers));
      if (createdBanks.length <= 1) {
        unmarkStepCompleted(WizardStep.Banks);
      }
      // Reset form so user can immediately create a replacement
      setEditingBankId(null);
      setForm(getDefaultFormState());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepBanks] DELETE bank failed:", err);
      const msg = err instanceof Error ? err.message : String(err);
      setStepError(`Failed to delete bank — ${msg}`);
    }
  };

  /**
   * Runs after DeletedBanksPanel has restored a bank: the mirror image of
   * `handleDeleteBank`.
   *
   * The restored bank is back in every list the later steps read, so what they
   * concluded while it was gone no longer holds. Their completion flags are
   * cleared over the same range a delete clears, for the same reason: a step
   * left complete would keep Review's "finish" and give the forward-jump guard a
   * one-click hop past everything this restore just re-opened. The flags are
   * cleared before the reload, so a reload that fails cannot leave them set.
   * The reload then brings the bank into the "Created Banks" table and the
   * wizard's own bank lists, which is also what marks this step complete again.
   */
  const handleBankRestored = async () => {
    setStepError(null);
    unmarkStepCompleted(...wizardStepsFrom(WizardStep.ChannelModifiers));
    if (!wizardState.serverId) return;
    try {
      const token = await GETJwtToken();
      const bankResult = await triggerGetBanksByServer({
        idServer: wizardState.serverId,
        page: 1,
        limit: 9999,
        searchAndFilterString: "",
        token,
      });
      // A lazy trigger resolves with `error` on an HTTP failure instead of
      // throwing. Without this a failed reload would read as "no banks" and
      // empty the Created Banks table the operator was looking at.
      if (bankResult.error) throw bankResult.error;
      const rows: CreatedBank[] = (bankResult.data?.data || []).map((b: any) =>
        toCreatedBankRow(b, channels, adapters),
      );
      setCreatedBanks(rows);
      const links = (bankResult.data?.data || []).map((b: any) => bankAdapterLink(b, adapters));
      setWizardState((prev) => {
        const updated = structuredClone(prev);
        rows.forEach((row) => {
          if (!updated.bankIds.includes(row.id)) updated.bankIds.push(row.id);
          updated.bankNames[row.id] = row.name;
          updated.bankTypes[row.id] = row.type;
        });
        return { ...updated, ...withBankAdapterLinks(updated, links) };
      });
    } catch (err) {
      setStepError(
        `The bank was restored, but the list could not be reloaded — ${describeError(err)}`,
      );
    }
  };

  /**
   * Point the node->lift matrix at the adapter the form currently names.
   *
   * Two entry points select an adapter — the create-mode dropdown and
   * `handleEditBank`'s hydrate — and both have to end in the same OPC UA state
   * or the matrix exists for one and not the other. Setting the flag only from
   * the edit path is what made it unreachable while *creating* an OPC UA bank:
   * the render gate reads `isOpcUaProto`, so the operator had to add the bank,
   * reopen it, and save again before the mapping could be entered at all.
   *
   * The node list comes from the adapter's binary CDO — OPC UA cannot go
   * through the JSON `cdo_input`/`cdo_template` path — which is a different
   * endpoint from the adapter fetch both callers already make. `token` is
   * whatever the caller has already resolved, so an adapter selection costs no
   * second token fetch; pass null when it has none and this fetches its own.
   */
  const applyOpcUaAdapterState = async (
    adapterProto: string,
    adapterId: string | undefined,
    token: string | null,
  ): Promise<void> => {
    const useOpcUa = adapterProto === OPC_UA_PROTOCOL;
    setIsOpcUaProto(useOpcUa);
    if (!useOpcUa || !adapterId) {
      setOpcUaAdapterMapping(null);
      return;
    }
    try {
      const jwt = token ?? (await GETJwtToken());
      const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
      const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, jwt);
      setOpcUaAdapterMapping(bytes && bytes.length > 0 ? decodeOpcUaMapping(bytes) : null);
    } catch {
      setOpcUaAdapterMapping(null);
    }
  };

  const handleEditBank = async (bankId: string, forceRefresh = false) => {
    const token = await GETJwtToken();
    const result = await triggerGetBank(
      forceRefresh ? { bankId, token, _refreshAt: Date.now() } : { bankId, token },
    );
    const d = result.data?.data || result.data;
    if (!d) return;
    setManualBankFloorEditingEnabled(false);
    setEditingBankData(d);
    // Real saved-lift count, not the bank's own numberOfLifts column below —
    // that column is just a user-declared target and can already disagree
    // with reality (e.g. a lift POST that failed partway through a previous
    // edit). d.lifts is the same raw array StepLiftsFloors reads off this
    // same GET /banks/{id} response to decide which lifts already exist.
    setExistingLiftCount(Array.isArray(d.lifts) ? d.lifts.length : 0);
    setInitialSimpleBaseOffsets(null);
    const channelId = formatUuidT(d.channelId) || d.channel_id || "";
    const adapterId = formatUuidT(d.adapterId) || d.adapter_id || "";
    const numberOfLifts = Math.min(d.numberOfLifts ?? d.number_of_lifts ?? 1, 1024);
    const floorCount = Math.min(d.floorCount ?? d.floor_count ?? 2, 1024);
    const floorBelowLobby = d.floorBelowLobby ?? d.floor_below_lobby ?? 0;
    const startFloorNo = d.startFloorNo ?? d.start_floor_no ?? 1;
    const frontDoors = d.frontDoors ?? d.front_doors ?? true;
    const rearDoors = d.rearDoors ?? d.rear_doors ?? false;
    const visible = d.visible ?? true;
    const bankOverlay = readBankOverlay(d);
    // `??`, not `||`: 0 is the bottom of the LobbyVision2 range, a port like any
    // other. On the FlatBuffer wire an absent port and 0 are the same bytes
    // (archive/bank.fbs gives logical_port no null default, and LiftCloud packs
    // a NULL column as 0), so a bank stored without a port shows 0 here too —
    // the value UpdateBank packs for an omitted port anyway. Only a JSON `null`
    // stays "Not set".
    const logicalPort = d.logicalPort ?? d.logical_port ?? null;
    const eventReportingAddress = d.eventReportingAddress || d.event_reporting_address || null;
    // Resolved once and reused below for the pin-assignment matrix's lift
    // name fallback, so it stays in lockstep with the form's own devType.
    const resolvedDevType =
      typeof d.type === "number"
        ? devTypeFromString(LiftType[d.type as LiftType] ?? LiftType[LiftType.ELEVATOR])
        : devTypeFromString(d.type || LiftType[LiftType.ELEVATOR]);
    setForm({
      name: d.name || "",
      devType: resolvedDevType,
      channel_id: channelId,
      adapter_id: adapterId,
      number_of_lifts: numberOfLifts,
      floor_count: floorCount,
      floor_below_lobby: floorBelowLobby,
      start_floor_no: startFloorNo,
      stops: d.stops ?? 2,
      front_doors: frontDoors,
      rear_doors: rearDoors,
      visible,
      logical_port: logicalPort,
      event_reporting_address: eventReportingAddress,
      vendor_location_path: d.vendorLocationPath || d.vendor_location_path || null,
      vendor_site_path: d.vendorSitePath || d.vendor_site_path || null,
      vendor_group_path: d.vendorGroupPath || d.vendor_group_path || null,
      overlay_bank_id: bankOverlay.overlayBankId || null,
      overlay_lifts: bankOverlay.overlayLifts,
      stopsLinked: floorCount === d.stops,
    });

    // Snapshot the editable fields (in BANK_FIELD_RULES key shape) so a
    // post-save diff can decide which downstream wizard steps actually
    // need re-verification. Keys here are camelCase to match the
    // BANK_FIELD_RULES rule table; the post-save snapshot in
    // handleUpdateBank uses the same keys.
    let parsedOffsetMapping: unknown = null;
    const rawOm = d.offsetMappingJson ?? d.offset_mapping;
    if (typeof rawOm === "string") {
      try {
        parsedOffsetMapping = JSON.parse(rawOm);
      } catch {
        parsedOffsetMapping = rawOm;
      }
    } else if (rawOm != null) {
      parsedOffsetMapping = rawOm;
    }
    // Door capability is snapshotted per floor, not from the bank's own
    // front_doors / rear_doors columns. Those columns are write-once at
    // create: PUT /banks/{bankID} never writes them (handleUpdateBank's body
    // omits them, and LiftCloud's three update paths all build their `updates`
    // map with no door key — see utils/bankDoorSignature.ts's header comment
    // for the verified detail), so on a bank whose floors were edited after
    // creation the column is permanently out of date. Diffing that frozen
    // column here against the floor-derived value in handleUpdateBank would
    // mis-fire on EVERY save of such a bank, not once — the save never
    // corrects the column, so the disagreement comes straight back.
    //
    // frontDoors / rearDoors above are the same values the bankFloorDoorRows
    // builder uses as its per-row fallback, passed through here for exactly
    // that purpose and nothing more.
    const doorSignature = bankDoorSignatureFromRawFloors(
      d.bank_floors ?? d.bankFloors,
      frontDoors,
      rearDoors,
    );
    originalBankSnapshotRef.current = {
      name: d.name || "",
      numberOfLifts,
      floorCount,
      floorBelowLobby,
      startFloorNo,
      channelId,
      adapterId,
      frontDoors: doorSignature.front,
      rearDoors: doorSignature.rear,
      offsetMapping: parsedOffsetMapping,
      active: d.active !== false,
    };

    setEditingBankId(bankId);

    // Load disabled columns from bank (FlatBuffer: disabledColumnsJson as string)
    let rawDisabled = d.disabledColumnsJson ?? d.disabled_columns;
    if (typeof rawDisabled === "string") {
      try {
        rawDisabled = JSON.parse(rawDisabled);
      } catch {
        rawDisabled = null;
      }
    }
    const savedDisabled = Array.isArray(rawDisabled)
      ? new Set<string>(
          rawDisabled.filter((value: unknown): value is string => typeof value === "string"),
        )
      : new Set<string>();
    setDisabledColumns(savedDisabled);

    // Resolve adapter — fetch the full adapter from the single-adapter
    // endpoint which includes CDO data (the list endpoint skips it).
    const parseSafe = (v: unknown) => {
      if (!v) return null;
      if (typeof v === "object") return v;
      try {
        return JSON.parse(v as string);
      } catch {
        return null;
      }
    };
    const localAd = adapters.find((a) => a.id === adapterId);
    let adForMatrix: any =
      d.adapter ??
      (localAd
        ? {
            ...localAd,
            offset_mapping: parseSafe(localAd.offset_mapping),
            intra_content_flags: parseSafe(localAd.intra_content_flags),
            cdo_input: parseSafe(localAd.cdo_input),
            cdo_template: parseSafe(localAd.cdo_template),
          }
        : null);
    // Fetch full adapter + keys in parallel for CDO data
    if (adapterId) {
      try {
        const [fullAdResult, keysResult] = await Promise.all([
          triggerGetAdapter({ id: adapterId, token }).unwrap(),
          triggerGetAdapterKeys({ adapterId, token }).unwrap(),
        ]);
        const fullAd = fullAdResult?.data || fullAdResult;
        if (fullAd) {
          adForMatrix = {
            ...adForMatrix,
            ...fullAd,
            offset_mapping:
              parseSafe(fullAd.offsetMappingJson ?? fullAd.offset_mapping) ??
              adForMatrix?.offset_mapping,
            intra_content_flags:
              parseSafe(fullAd.intraContentFlagsJson ?? fullAd.intra_content_flags) ??
              adForMatrix?.intra_content_flags,
            cdo_input: parseSafe(fullAd.cdoInputJson ?? fullAd.cdo_input) ?? adForMatrix?.cdo_input,
            cdo_template:
              parseSafe(fullAd.cdoTemplateJson ?? fullAd.cdo_template) ?? adForMatrix?.cdo_template,
            keys: keysResult?.data || fullAd.keys || adForMatrix?.keys || [],
          };
        } else if (adForMatrix && !adForMatrix.keys?.length) {
          adForMatrix = { ...adForMatrix, keys: keysResult?.data || [] };
        }
      } catch {
        // Full adapter fetch failed — continue with list/embedded data
        if (adForMatrix && !adForMatrix.keys?.length) {
          try {
            const keysResult = await triggerGetAdapterKeys({ adapterId, token }).unwrap();
            adForMatrix = { ...adForMatrix, keys: keysResult?.data || [] };
          } catch {
            // Keys not available
          }
        }
      }
    }

    // Every matrix input in one derivation. Intra-content is per-adapter only —
    // bank-level flags are deliberately not consulted.
    const types = applyAdapterMatrixInputs(deriveAdapterMatrixInputs(adForMatrix));
    const cdoData = adForMatrix?.cdo_template || adForMatrix?.cdo_input;

    // Stash the full adapter data so `buildAdapterDefaults2Row` (called
    // during render) can read the offset_mapping the list endpoint omits.
    setCurrentBankAdapter(adForMatrix ?? null);

    // Always derive adapter defaults first, then overlay any saved bank
    // offsets. This ensures no data is lost if the bank save only captured
    // a subset of columns, and new adapter columns automatically get defaults.
    const numLifts = Math.min(d.numberOfLifts ?? d.number_of_lifts ?? 1, 1024);
    const adapterDefaults = deriveOffsetsFromAdapter(adForMatrix, types, numLifts);
    setAdapterDefaultOffsets(adapterDefaults);
    const bankOm = parseSafe(d.offsetMappingJson) ?? parseSafe(d.offset_mapping);
    const saved = keyedMapsToLiftOffsets(bankOm, types);
    const merged: Record<string, number>[] = Array.from({ length: numLifts }, (_, i) => {
      const base = adapterDefaults[i] || {};
      const overlay = saved[i] || {};
      return { ...base, ...overlay };
    });
    const simplifiedBaseline = resolveSimplifiedBaseline(adapterDefaults, merged);
    setLiftOffsets(merged);
    setAdapterDefaultOffsets(simplifiedBaseline);
    setInitialSimpleBaseOffsets(
      deriveSimplifiedBaseOffsets(merged, simplifiedBaseline, types, savedDisabled),
    );

    // Pin assignment matrix: load adapter CDO entries for Sequent/Numato
    //
    // Bug: this previously did `(adForMatrix?.protocolType ?? adForMatrix
    // ?.protocol_type ?? "").toString().toUpperCase()`, which breaks for
    // every adapter whose protocol arrives as the FB Object API's numeric
    // enum discriminant — most visibly MCE (`AdapterProtocol.MCE === 0`):
    // `(0).toString().toUpperCase()` is `"0"`, never `"MCE"`, so `isMceProto`
    // was always false and the Event Reporting Address field never rendered
    // for an MCE-assigned bank. `resolveAdapterProtocolUpper` (used
    // elsewhere in this file for the same MODBUS-detection problem, see its
    // doc comment above) normalizes string and numeric protocol shapes alike.
    const adapterProto = resolveAdapterProtocolUpper(adForMatrix);
    const usePinAssignment = PIN_ASSIGNMENT_PROTOS.has(adapterProto);
    setIsPinProto(usePinAssignment);
    if (usePinAssignment && cdoData) {
      const entries: DiscreteToLn2EntryInterface[] = cdoData.discrete_2_message ?? [];
      setPinEntries(entries);
      const aid = formatUuidT(adForMatrix?.id) || adForMatrix?.id || "";
      setPinAdapterId(aid);
    }

    // Pin assignment matrix: load this bank's REAL lifts. The matrix must
    // never be fed synthesized index ids ("0", "1", ...) — LiftCloud's
    // packer can't parse those as a UUID, silently collapses lift_id to the
    // zero UUID, and CDO generation drops the pin. Decoupled from cdoData
    // above: lifts are a property of the bank, not the adapter's CDO.
    if (usePinAssignment) {
      try {
        const liftsResult = await triggerGetLiftsByBank({
          bankId,
          page: 1,
          limit: 9999,
          searchAndFilterString: "",
          token,
        });
        const liftsData = (liftsResult.data?.data ?? liftsResult.data ?? []) as any[];
        const realLifts: PinAssignmentLift[] = liftsData
          .map((l: any) => ({
            id: formatUuidT(l.id) || String(l.id ?? ""),
            position: typeof l.position === "number" ? l.position : 0,
            carNum: typeof l.carNum === "number" ? l.carNum : (l.car_num ?? 0),
            rawName: l.name,
          }))
          .filter((l) => l.id !== "")
          .sort((a, b) => a.position - b.position)
          .map((l) => ({
            id: l.id,
            position: l.position,
            name: resolveLiftDisplayName(
              { name: l.rawName, carNum: l.carNum, position: l.position },
              resolvedDevType,
            ),
          }));
        setBankLifts(realLifts);
      } catch {
        setBankLifts([]);
      }
    } else {
      setBankLifts([]);
    }

    // Node -> lift matrix: load the adapter's OPC UA node list (binary CDO —
    // OPC UA cannot go through the JSON cdo_input/cdo_template path) and
    // this bank's saved bindings.
    setIsOpcPathProto(OPC_PATH_PROTOCOLS.has(adapterProto));
    setIsMceProto(adapterProto === "MCE");
    await applyOpcUaAdapterState(adapterProto, adapterId, token);
    const rawNodeLiftMappingBinary =
      d.opcUaNodeLiftMappingBinary ?? d.opc_ua_node_lift_mapping_binary;
    setOpcUaNodeLiftMapping(decodeOpcUaNodeLiftMappingBinary(rawNodeLiftMappingBinary));
  };

  /**
   * Delete the cars a lowered "Number of Lifts" no longer wants, highest
   * position first, and mirror the bookkeeping StepLiftsFloors' `handleDeleteLift`
   * does for the same operation.
   *
   * Stops at the FIRST failure and reports it: a bank that has lost three rows
   * and then keeps its old count is exactly as desynced as one that lowered the
   * count and deleted nothing, so a partial run must not be followed by the
   * count write. Returns false in that case and the caller leaves the bank
   * unsaved.
   */
  const deleteSurplusLifts = async (surplus: SurplusLift[], token: string): Promise<boolean> => {
    if (!editingBankId) return false;
    const bankId = editingBankId;
    const deletedIds: string[] = [];
    let failedAt = -1;
    let failure: unknown = null;
    // The same indexed loop the grow path in `persistBankUpdate` uses: these
    // deletes are ordered (highest position first) and must stop at the first
    // failure, so they cannot be fired off concurrently.
    for (let i = 0; i < surplus.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await triggerDeleteLift({ id: surplus[i].id, token }).unwrap();
        deletedIds.push(surplus[i].id);
      } catch (err) {
        failedAt = i;
        failure = err;
        break;
      }
    }
    if (failedAt >= 0) {
      // Reflect what DID go away, or the wizard's per-lift pickers keep
      // pointing at ids that no longer exist.
      forgetLiftIds(bankId, deletedIds);
      setStepError(
        `Lift count not changed — car ${surplus[failedAt].position} could not be deleted (${describeError(
          failure,
        )}). ${
          deletedIds.length > 0
            ? `${deletedIds.length} of ${surplus.length} lift(s) were already deleted, so re-open this bank to see its real cars before saving again.`
            : "No lifts were deleted and the bank was not saved."
        }`,
      );
      return false;
    }
    forgetLiftIds(bankId, deletedIds);
    return true;
  };

  /**
   * Drop deleted lifts from the wizard state that Notifications and Actions
   * read to build their per-lift pickers, and unmark those two steps — the
   * exact invalidation `handleDeleteLift` in StepLiftsFloors.tsx performs, so a
   * rule bound to a car that no longer exists is flagged for review instead of
   * silently pointing at a dead id.
   */
  const forgetLiftIds = (bankId: string, liftIds: string[]) => {
    if (liftIds.length === 0) return;
    setWizardState((prev) => {
      const updated = structuredClone(prev);
      updated.liftIdsByBank[bankId] = (updated.liftIdsByBank[bankId] ?? []).filter(
        (id: string) => !liftIds.includes(id),
      );
      liftIds.forEach((liftId) => {
        if (updated.liftNamesByBank[bankId]) {
          delete updated.liftNamesByBank[bankId][liftId];
        }
      });
      return updated;
    });
    unmarkStepCompleted(WizardStep.Notifications, WizardStep.Actions);
  };

  /**
   * The destructive half of lowering "Number of Lifts": confirm, then delete
   * the surplus cars and only after that write the smaller count.
   *
   * `Modal.confirm` is this file's existing affordance for an action that is
   * triggered imperatively rather than from a dedicated row button (see
   * `handleDeleteBankFloor` and the manual-editing toggle); the per-row
   * deletes elsewhere in the wizard use a `Popconfirm` because they ARE a row
   * button. Confirming here is not optional — it destroys server rows.
   */
  const confirmLiftReduction = (surplus: SurplusLift[], token: string) => {
    const bankId = editingBankId;
    if (!bankId) return;
    const unresolvable = surplus.filter((lift) => lift.id === "");
    if (unresolvable.length > 0) {
      // A loaded row with no usable id cannot be deleted, so the count can
      // never be made to match the rows. Say so instead of half-applying it.
      setStepError(
        `Lift count not changed — ${unresolvable.length} of the ${surplus.length} car(s) this bank ` +
          "would have to give up has no saved id, so it cannot be deleted. Re-open the bank and try again.",
      );
      return;
    }
    Modal.confirm({
      title: `Delete ${surplus.length} lift${surplus.length === 1 ? "" : "s"}?`,
      content: (
        <div>
          <p>
            This bank currently has {surplus.length + form.number_of_lifts} saved car(s). Saving at{" "}
            {form.number_of_lifts} will permanently delete {surplus.length} of them:{" "}
            {surplus
              .map((lift) => `position ${lift.position}${lift.name ? ` (${lift.name})` : ""}`)
              .join(", ")}
            .
          </p>
          <p>
            Their floor stops go with them, and any notification or action wired to those cars will
            need re-checking.
          </p>
        </div>
      ),
      width: 560,
      okText: "Delete lifts",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        setStepError(null);
        if (!(await deleteSurplusLifts(surplus, token))) return;
        const saved = await persistBankUpdate(token);
        if (!saved) {
          setStepError(
            `${surplus.length} lift(s) were deleted, but the bank's new lift count could not be saved. ` +
              "Re-open this bank and save again so its count matches its cars.",
          );
        }
      },
    });
  };

  /**
   * Save an edited bank. Growing "Number of Lifts" auto-creates the missing
   * cars (see `persistBankUpdate`); lowering it deletes the surplus cars first,
   * behind a confirmation, because the count and the rows must never be allowed
   * to disagree — see `selectSurplusLifts`.
   */
  const handleUpdateBank = async () => {
    if (!isFormValid() || !editingBankId) return;
    const token = await GETJwtToken();
    const surplus = selectSurplusLifts(editingBankData?.lifts, form.number_of_lifts);
    if (surplus.length > 0) {
      confirmLiftReduction(surplus, token);
      return;
    }
    await persistBankUpdate(token);
  };

  /**
   * The full-record PUT of an edited bank and everything that follows it: the
   * auto-create of newly-declared cars, the pin save, read-back verification
   * and the wizard bookkeeping. Returns false only when the bank record itself
   * could not be written.
   */
  const persistBankUpdate = async (token: string): Promise<boolean> => {
    if (!editingBankId) return false;
    // Reached from two places (the direct save and the lift-reduction confirm),
    // so the range gate lives here as well as in isFormValid: an out-of-range
    // port must never be handed to the serializer.
    if (logicalPortInvalid) {
      setStepError(logicalPortRangeMessage());
      return false;
    }
    const body: Record<string, unknown> = {
      id: editingBankId,
      name: form.name,
      type: typeLabel,
      channel_id: form.channel_id,
      adapter_id: form.adapter_id,
      org_id: wizardState.organizationId,
      number_of_lifts: form.number_of_lifts,
      floor_count: form.floor_count,
      floor_below_lobby: form.floor_below_lobby,
      start_floor_no: form.start_floor_no,
      stops: form.stops,
      visible: form.visible,
      active: true,
      interface: null,
    };
    if (bankFloorDoorRows.length > 0) {
      body.bank_floors = bankFloorDoorRows.map((bf) => ({
        id: bf.id,
        name: bf.name,
        floor_no: bf.floorNo,
        floor_index: bf.floorIndex,
        position: bf.position,
        floor_below_lobby: bf.floorBelowLobby,
        front_door: bf.frontDoor,
        rear_door: bf.rearDoor,
      }));
    }
    if (form.logical_port != null) {
      body.logical_port = form.logical_port;
    }
    // For an OPC-addressed bank the wizard is the authoritative editor of the
    // three vendor path segments: it re-sends all three on every save (empty
    // string for a cleared input) and flags the set as provided, so the server
    // writes each to its value or clears it to NULL. Any other protocol does not
    // show these fields, so the payload omits them and the server's guarded
    // write leaves any stored columns untouched.
    if (isOpcPathProto) {
      OPC_VENDOR_PATH_FIELDS.forEach((field: OpcVendorPathField) => {
        const value = form[field];
        body[field] = value != null ? value.trim() : "";
      });
      body.vendor_paths_provided = true;
    }
    // Authoritative over the overlay on every save, exactly as handleAddBank:
    // a cleared "Draw on bank" sends the flag alone, which clears it.
    Object.assign(body, overlaySaveFields(form.overlay_bank_id, form.overlay_lifts));

    if (form.event_reporting_address != null && form.event_reporting_address.trim() !== "") {
      body.event_reporting_address = form.event_reporting_address.trim();
    }
    if (liftOffsets.length > 0) {
      // Save ALL columns (including disabled) so values are preserved
      // when the user re-enables a column later. The disabled_columns
      // list tells the backend which columns to skip at expansion time.
      body.offset_mapping_json = JSON.stringify(
        liftOffsetsToKeyedMaps(liftOffsets, mappedTypes, categoryToRegType),
      );
    }
    // Sent unconditionally (unlike offset_mapping_json above): disabledColumns
    // is independent local state — the offset matrix's column-toggle checkbox
    // updates it without ever touching liftOffsets, so a user can disable a
    // column before entering any offset value. Gating this on
    // liftOffsets.length > 0 would silently drop that choice.
    body.disabled_columns_json = JSON.stringify(Array.from(disabledColumns));
    if (isOpcUaProto) {
      body.opc_ua_node_lift_mapping_binary = opcUaNodeLiftIndexBindingsToBase64(
        buildOpcUaNodeLiftIndexBindingsBytes(nodeLiftMapToValidEntries(opcUaNodeLiftMapping)),
      );
    }
    try {
      await triggerPutBank({ id: editingBankId, body, token }).unwrap();

      // LIFT-791: auto-create any newly-added lifts. Growing "Number of
      // Lifts" past what this bank already has (gated to grow-only by the
      // input's own min={existingLiftCount} below) is otherwise a dead end —
      // StepLiftsFloors only synthesizes blank lift rows for a bank that has
      // ZERO lifts yet, so a bank being edited never gets rows for the
      // newly-declared count. POSTing the missing lift(s) here, the same way
      // a brand-new bank's first lifts eventually get created, means they
      // show up as ordinary existing lifts the next time this bank is opened
      // on the Lifts & Floors step, where their name/floors can be filled in.
      //
      // type must be the numeric LiftType enum, NOT typeLabel's string form:
      // CreateLift's serializer reads `type` via intEnum(), which silently
      // defaults to 0 (ELEVATOR) for anything non-numeric — sending the
      // string label here would misclassify every non-elevator lift.
      const liftsToCreate = form.number_of_lifts - existingLiftCount;
      if (liftsToCreate > 0) {
        try {
          const liftTypeValue = LiftType[typeLabel as keyof typeof LiftType];
          for (let i = 0; i < liftsToCreate; i += 1) {
            const position = existingLiftCount + i + 1;
            // eslint-disable-next-line no-await-in-loop
            await triggerPostLift({
              body: {
                name: `Car ${position}`,
                position,
                type: liftTypeValue,
                bankId: editingBankId,
                organizationId: wizardState.organizationId,
              },
              token,
            }).unwrap();
          }
        } catch (liftErr) {
          // eslint-disable-next-line no-console
          console.error("[StepBanks] Auto-creating new lifts failed:", liftErr);
          setStepError(
            `Bank saved, but ${liftsToCreate} new lift(s) could not be created automatically — ` +
              "add them from the Lifts & Floors step.",
          );
        }
      }

      // Save pin assignments via the bank-scoped endpoint. Unlike the removed
      // adapter-CDO write, a failure here MUST block: silently reporting the
      // bank as saved while its pins are unwritten would leave the operator
      // believing the bank is configured when LiftCloud still has no way to
      // attribute this adapter's traffic to a lift.
      if (isPinProto && pinAdapterId && pinEntries.length > 0) {
        try {
          await triggerPutBankPinAssignments({
            bankId: editingBankId,
            body: {
              adapter_id: pinAdapterId,
              assignments: buildPinAssignmentPayload(pinEntries, editingBankId),
            },
            token,
          }).unwrap();
        } catch (pinErr) {
          // eslint-disable-next-line no-console
          console.error("[StepBanks] Failed to save pin assignments:", pinErr);
          setStepError(
            "Bank saved, but pin assignments could not be written — this bank has no usable pin mapping until this is corrected.",
          );
          // The bank record itself DID land, so the count and the rows agree —
          // the shrink path must not additionally warn about an unwritten count.
          return true;
        }
      }

      // Read-back verification: non-blocking, does not affect the success path.
      // Uses .then() directly (not .unwrap()) to work with both RTK QueryActionCreatorResult
      // and plain Promise test mocks.
      triggerGetBank({ bankId: editingBankId, token })
        .then((result: any) => {
          const bankData = result?.data?.data || result?.data || result;
          const readName = bankData?.name || "";
          if (readName !== form.name) {
            // eslint-disable-next-line no-console
            console.error(
              `[StepBanks] Verification failed: expected name "${form.name}", got "${readName}"`,
            );
            setStepError("Bank update could not be verified — please check the data is correct");
          }
        })
        .catch(() => {
          // Non-critical — verification failure does not block success
          setStepError("Bank update could not be verified — please check the data is correct");
        });

      setCreatedBanks((prev) =>
        prev.map((b) =>
          b.id === editingBankId
            ? {
                ...b,
                name: form.name,
                type: typeLabel,
                channelName: getChannelNameById(form.channel_id),
                channelMode: getChannelModeById(form.channel_id),
                channelActiveEndpointName: null,
                adapterName: getAdapterNameById(form.adapter_id),
                numberOfLifts: form.number_of_lifts,
                channelId: form.channel_id,
                overlayBankId: form.overlay_bank_id ?? "",
              }
            : b,
        ),
      );
      setWizardState((prev) => ({
        ...prev,
        bankNames: { ...prev.bankNames, [editingBankId]: form.name },
        bankTypes: { ...prev.bankTypes, [editingBankId]: typeLabel },
        ...withBankAdapterLinks(prev, [
          {
            bankId: editingBankId,
            adapterId: form.adapter_id,
            protocol: assignedAdapterProtocol,
          },
        ]),
      }));
      if (standalone && onSaveSuccess) {
        onSaveSuccess();
        return true;
      }
      resetOffsetEditorState();
      markStepCompleted(4);

      // Smart downstream invalidation: only unmark wizard steps whose
      // validity actually depends on the fields the user changed. Compare
      // the post-save state against the snapshot taken in handleEditBank.
      // If only the bank name changed, no downstream step is affected;
      // if numberOfLifts changed, Lifts/Notifications/Actions are; etc.
      // See utils/wizardInvalidation.ts for the field→step rule table.
      // Same per-floor derivation as the pre-save snapshot, over the rows
      // that were just written as body.bank_floors above. Reading
      // form.front_doors / form.rear_doors here instead would compare an
      // OR-aggregate against a per-floor signature, and would additionally
      // report a change for a bank-level toggle on a floorless bank — where
      // body.bank_floors is never set and nothing door-related is persisted
      // at all.
      //
      // bankFloorDoorRows is populated by a separate useEffect (keyed on
      // editingBankId/editingBankData), not synchronously by handleEditBank
      // — so it CAN still be empty here if a save is triggered before that
      // effect has run (a human cannot realistically win this race, but an
      // automated click — e2e/Playwright — can). Falling back to the
      // pre-save snapshot's own door signature when the live state is empty
      // — the exact same pattern offsetMapping already uses two lines below
      // — reads as "no visible change" instead of a false empty-vs-populated
      // diff. This can't hide a real floor-count change: that's caught
      // independently by floorCount's own rule below, regardless of what
      // the door signature reports.
      const savedDoorSignature =
        bankFloorDoorRows.length > 0 ? bankDoorSignatureFromRows(bankFloorDoorRows) : null;
      const updatedSnapshot: Record<string, unknown> = {
        name: form.name,
        numberOfLifts: form.number_of_lifts,
        floorCount: form.floor_count,
        floorBelowLobby: form.floor_below_lobby,
        startFloorNo: form.start_floor_no,
        channelId: form.channel_id,
        adapterId: form.adapter_id,
        frontDoors: savedDoorSignature?.front ?? originalBankSnapshotRef.current?.frontDoors,
        rearDoors: savedDoorSignature?.rear ?? originalBankSnapshotRef.current?.rearDoors,
        offsetMapping:
          liftOffsets.length > 0
            ? liftOffsetsToKeyedMaps(liftOffsets, mappedTypes, categoryToRegType)
            : (originalBankSnapshotRef.current?.offsetMapping ?? null),
        active: true,
      };
      const stepsToInvalidate = computeInvalidatedSteps(
        originalBankSnapshotRef.current ?? undefined,
        updatedSnapshot,
        BANK_FIELD_RULES,
      );
      if (stepsToInvalidate.length > 0) {
        unmarkStepCompleted(...stepsToInvalidate);
      }

      originalBankSnapshotRef.current = null;
      setEditingBankId(null);
      setExistingLiftCount(0);
      setForm(getDefaultFormState());
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StepBanks] PUT bank update failed:", err);
      // Surface the server's own reason (a 400 for an out-of-range Logical Port
      // says which range, for instance) instead of leaving the operator with a
      // save button that simply did nothing. describeError reads the status and
      // the response message out of the RTK Query error shape.
      setStepError(`Bank update failed — ${describeError(err)}`);
      return false;
    }
  };

  const cancelEdit = () => {
    if (standalone && onCancel) {
      onCancel();
      return;
    }
    resetOffsetEditorState();
    setEditingBankId(null);
    setExistingLiftCount(0);
    setForm(getDefaultFormState());
  };

  const handleGlobalFrontDoorsChange = (checked: boolean) => {
    if (!checked && !form.rear_doors) {
      message.error("A bank must have at least Front or Rear doors enabled.");
      return;
    }
    setForm((prev) => ({ ...prev, front_doors: checked }));
    setBankFloorDoorRows((prev) =>
      prev.map((row) => ({
        ...row,
        frontDoor: checked,
      })),
    );
  };

  const handleGlobalRearDoorsChange = (checked: boolean) => {
    if (!checked && !form.front_doors) {
      message.error("A bank must have at least Front or Rear doors enabled.");
      return;
    }
    setForm((prev) => ({ ...prev, rear_doors: checked }));
    setBankFloorDoorRows((prev) =>
      prev.map((row) => ({
        ...row,
        rearDoor: checked,
      })),
    );
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */

  // Standalone mode: auto-load the bank and populate serverId in wizard state
  // so the channel/adapter queries fire.
  const standaloneLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!editBankId || !standalone || standaloneLoadedRef.current) return;
    standaloneLoadedRef.current = true;
    const autoLoad = async () => {
      const token = await GETJwtToken();
      const result = await triggerGetBank({ bankId: editBankId, token });
      const d = result.data?.data || result.data;
      if (!d) return;

      // Set serverId so the hydrate effect can fetch channels/adapters.
      // Archive.Bank names its channel, not its server: resolve through it.
      const { serverId, organizationId } = await resolveStandaloneBankScope(d, (channelId) =>
        triggerGetChannel({ channelId, token }).unwrap(),
      );
      if (serverId) {
        setWizardState((prev) => ({
          ...prev,
          serverId,
          organizationId: organizationId || prev.organizationId,
        }));
      }

      // Reuse the existing edit handler once serverId is set
      // Small delay lets the wizard state propagate so channel/adapter queries fire
      setTimeout(() => handleEditBank(editBankId), 100);
    };
    autoLoad();
  });

  useEffect(() => {
    if (!editingBankId || !editingBankData) {
      setBankFloorDoorRows([]);
      return;
    }
    const raw: any[] = editingBankData.bank_floors ?? editingBankData.bankFloors ?? [];
    if (!Array.isArray(raw)) {
      setBankFloorDoorRows([]);
      return;
    }
    const nextRows = raw
      .map((bf: any, idx: number): BankFloorDoorRow => {
        const normalizedId =
          formatUuidT(bf?.id) ||
          formatUuidT(bf?.ID) ||
          (typeof bf?.id === "string" ? bf.id : "") ||
          (typeof bf?.ID === "string" ? bf.ID : "") ||
          `bf-${idx}`;
        const positionValue = bf?.position ?? bf?.floorNo ?? bf?.floor_no ?? idx;
        const floorNoValue = bf?.floorNo ?? bf?.floor_no ?? positionValue;
        const floorBelowLobbyValue = bf?.floorBelowLobby ?? bf?.floor_below_lobby ?? false;
        // floorIndex/frontDoor/rearDoor resolution is shared with
        // utils/bankDoorSignature.ts (resolveBankFloorDoorFields) — one
        // field-resolution chain both this builder and the invalidation
        // snapshot call, so they cannot drift apart.
        const doorFields = resolveBankFloorDoorFields(bf, idx, form.front_doors, form.rear_doors);
        return {
          key: `${normalizedId}-${idx}`,
          id: normalizedId,
          position: Number(positionValue) || 0,
          name: String(bf?.name ?? ""),
          floorNo: Number(floorNoValue) || 0,
          floorIndex: doorFields.floorIndex,
          floorBelowLobby: Boolean(floorBelowLobbyValue),
          frontDoor: doorFields.frontDoor,
          rearDoor: doorFields.rearDoor,
        };
      })
      .sort((a, b) => a.floorIndex - b.floorIndex);
    setBankFloorDoorRows(nextRows);
  }, [editingBankId, editingBankData]);

  // The "Front Door" / "Rear Door" column-header switches must reflect the
  // floors, not a separately-stored bank-level flag: form.front_doors and
  // form.rear_doors were seeded above from the bank's own front_doors/
  // rear_doors columns (a stale summary that can disagree with the floors
  // it is supposed to summarize — the reported case had every floor's
  // rear_door true while the bank's own rear_doors read false). Recomputing
  // here on every bankFloorDoorRows change — initial load AND every
  // individual floor toggle — keeps the header an accurate "is at least one
  // floor enabled" reflection in both directions: on when any floor turns
  // on, off when the last enabled floor turns off. This supersedes the
  // one-directional "flip the header on when a row turns on" patches in the
  // per-row Switch handlers below, which never flipped it back off.
  //
  // Deliberately independent of the frontDoors/rearDoors invalidation
  // signatures computed in handleEditBank/handleUpdateBank above (see
  // utils/bankDoorSignature.ts): this effect drives the header checkbox's
  // display value, that computation drives what gets diffed to decide
  // whether the Lifts & Floor step needs re-verification. They read the same
  // underlying floor rows but serve unrelated purposes — do not try to unify
  // them into one derivation.
  useEffect(() => {
    if (bankFloorDoorRows.length === 0) return;
    const anyFrontDoor = bankFloorDoorRows.some((row) => row.frontDoor);
    const anyRearDoor = bankFloorDoorRows.some((row) => row.rearDoor);
    setForm((prev) =>
      prev.front_doors === anyFrontDoor && prev.rear_doors === anyRearDoor
        ? prev
        : { ...prev, front_doors: anyFrontDoor, rear_doors: anyRearDoor },
    );
  }, [bankFloorDoorRows]);

  // Hydrate channels, adapters, then existing banks in order so name
  // resolution has the lookup arrays populated before bank rows are built.
  // Uses the list endpoint (getBanksByServer) instead of individual fetches
  // because FindBankByID's deep Preload chain can fail if any relationship
  // in Channel→Server→Org is broken.
  useEffect(() => {
    const hydrate = async () => {
      if (!wizardState.serverId) return;

      // 1. Fetch channels + adapters + banks by server in parallel.
      const token = await GETJwtToken();
      const [chList, adList, bankResult] = await Promise.all([
        fetchChannels(),
        fetchAdapters(),
        triggerGetBanksByServer({
          idServer: wizardState.serverId,
          page: 1,
          limit: 9999,
          searchAndFilterString: "",
          token,
        }),
      ]);

      // 2. Skip if already hydrated or no banks.
      if (createdBanks.length > 0) return;
      const banks = bankResult.data?.data || [];
      if (banks.length === 0) return;

      // 3. Build rows from flat bank objects + local name resolution, using
      // the freshly-fetched arrays (state may not be updated yet within this
      // same tick).
      const rows: CreatedBank[] = banks.map((b: any) => toCreatedBankRow(b, chList, adList));
      setCreatedBanks(rows);

      // Sync wizard state with the bank IDs we found.
      const bankIds = banks.map((b: any) => formatUuidT(b.id) || String(b.id ?? ""));
      const links = banks.map((b: any) => bankAdapterLink(b, adList));
      setWizardState((prev) => {
        const mergedIds = structuredClone(prev.bankIds);
        bankIds.forEach((id: string) => {
          if (!mergedIds.includes(id)) mergedIds.push(id);
        });
        return { ...prev, bankIds: mergedIds, ...withBankAdapterLinks(prev, links) };
      });
      if (banks.length > 0) markStepCompleted(4);
    };
    hydrate();
  }, [wizardState.serverId]);

  // Re-fetch channel and adapter dropdowns when items are added/removed
  // in prior wizard steps (keep-mounted means this component stays alive).
  useEffect(() => {
    if (!wizardState.serverId) return;
    fetchChannels();
    fetchAdapters();
  }, [wizardState.channelIds.length, wizardState.adapterIds.length]);

  // Re-fetch channels whenever the user navigates to this step so a channel
  // rename in StepChannels is immediately reflected in the dropdown.
  // putChannel invalidates the RTK Query cache but lazy queries don't
  // auto-refetch — this effect closes that gap. Entry-only is safe here even
  // though the old effect also fired on first mount: the
  // [wizardState.serverId] hydrate effect above already fetches channels on
  // initial load, independent of currentStep, so this effect only needs to
  // catch RETURN visits — exactly useWizardStepEntry's contract.
  useWizardStepEntry({
    step: WizardStep.Banks,
    onEnter: fetchChannels,
    enabled: !!wizardState.serverId,
  });

  // Re-mark step 4 as complete when banks exist (handles the case where
  // editing an adapter unmarked steps 4-7 but the banks are still valid).
  useEffect(() => {
    if (createdBanks.length > 0 || wizardState.bankIds.length > 0) {
      markStepCompleted(4);
    }
  }, [createdBanks.length, wizardState.bankIds.length]);

  useEffect(() => {
    if (!isSuccessPost || !dataPost) return;
    const bankData = dataPost.data || dataPost;
    const bankId = formatUuidT(bankData.id) || bankData.id;
    if (!bankId) return;

    const newBank: CreatedBank = {
      id: bankId,
      name: form.name,
      type: DEV_TYPE_LABELS[form.devType],
      channelName: getChannelNameById(form.channel_id),
      channelMode: getChannelModeById(form.channel_id),
      channelActiveEndpointName: null,
      adapterName: getAdapterNameById(form.adapter_id),
      numberOfLifts: form.number_of_lifts,
      channelId: form.channel_id,
      overlayBankId: form.overlay_bank_id ?? "",
    };

    setCreatedBanks((prev) => [...prev, newBank]);
    setWizardState((prev) => {
      const updated = structuredClone(prev);
      updated.bankIds = [...updated.bankIds, bankId];
      updated.bankNames = { ...updated.bankNames, [bankId]: form.name };
      updated.bankTypes = {
        ...updated.bankTypes,
        [bankId]: DEV_TYPE_LABELS[form.devType],
      };
      return {
        ...updated,
        ...withBankAdapterLinks(updated, [
          { bankId, adapterId: form.adapter_id, protocol: assignedAdapterProtocol },
        ]),
      };
    });
    markStepCompleted(4);
    resetOffsetEditorState();
    setForm(getDefaultFormState());
  }, [isSuccessPost, dataPost]);

  // ************************************************ */
  // TABLE COLUMNS ********************************* */
  const tableColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, record: CreatedBank) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <WizardEntityLink entityType="bank" entityId={record.id} />
          {name}
        </span>
      ),
    },
    {
      title: t("general.type"),
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Channel",
      dataIndex: "channelName",
      key: "channelName",
      render: (_: unknown, record: CreatedBank) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <ChannelModeBadge
            mode={record.channelMode}
            activeEndpointName={record.channelActiveEndpointName}
          />
          <span>{record.channelName}</span>
        </span>
      ),
    },
    {
      title: "Total Lifts",
      dataIndex: "numberOfLifts",
      key: "numberOfLifts",
      render: (_: unknown, record: CreatedBank) => (
        <Badge
          count={record.numberOfLifts}
          showZero
          overflowCount={9999}
          style={{ backgroundColor: "#1890ff" }}
        />
      ),
    },
    {
      title: "Adapter",
      dataIndex: "adapterName",
      key: "adapterName",
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_: unknown, record: CreatedBank) => (
        <div style={{ display: "flex", gap: 4 }}>
          <WizardOpGate op="update">
            <Button type="link" size="small" onClick={() => handleEditBank(record.id)}>
              Edit
            </Button>
          </WizardOpGate>
          <WizardOpGate op="delete">
            <Popconfirm
              title="Delete this bank?"
              onConfirm={() => handleDeleteBank(record.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" danger size="small" loading={isLoadingDelete}>
                Delete
              </Button>
            </Popconfirm>
          </WizardOpGate>
        </div>
      ),
    },
  ];

  const showBankFloorDoorTable =
    FLOOR_DEV_TYPES.has(form.devType) && !!editingBankId && bankFloorDoorRows.length > 0;

  // Derived, display-only ordering for the Bank Floors table (see
  // bankFloorSortDesc above). Sorted by `position`, ascending by default and
  // reversed when the invert-sort button is toggled. `bankFloorDoorRows`
  // itself is left untouched so row edits still resolve via `record.key`.
  const sortedBankFloorDoorRows = useMemo(() => {
    const sorted = [...bankFloorDoorRows].sort((a, b) => a.position - b.position);
    return bankFloorSortDesc ? sorted.reverse() : sorted;
  }, [bankFloorDoorRows, bankFloorSortDesc]);

  // "Draw on bank" (bank overlays). The step lists the banks of the wizard's
  // server, and a bank's server is its channel's, so a listed bank whose
  // channel is not one of this server's channels is left out rather than
  // trusted. The source-or-target rule is applied by overlayTargetChoice.
  const overlayChoice = useMemo(
    () => overlayTargetChoice(editingBankId, createdBanks, new Set(channels.map((ch) => ch.id))),
    [editingBankId, createdBanks, channels],
  );
  const overlayDrawnOnBy =
    overlayChoice.drawnOnBy.length > 0 ? overlayDrawnOnByMessage(overlayChoice.drawnOnBy) : null;

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin
      spinning={
        isLoadingPost || isLoadingPut || isLoadingChannels || isLoadingAdapters || isLoadingDelete
      }
    >
      <div data-testid="step-banks" style={{ padding: "16px" }}>
        {stepError && (
          <Alert
            type="error"
            showIcon
            closable
            message={stepError}
            onClose={() => setStepError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        {isErrorPost && (
          <Alert
            type="error"
            showIcon
            message="Failed to create bank — please check your inputs and try again."
            style={{ marginBottom: 16 }}
          />
        )}
        <DrawerRowDivider icon={faBuildingShield} title="Banks" />
        <WizardReadOnlyId label="Bank ID" value={editingBankId} />
        <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
          <div className="container">
            {/* Row 1: Name + Type */}
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <FormInputText
                  inputValue={form.name}
                  inputLabelName={t("general.name")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="name"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">{t("general.type")}</span>
                </div>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={form.devType}
                  onChange={(val: DevType) => {
                    const label = DEV_TYPE_LABELS[val];
                    setForm((prev) => ({
                      ...prev,
                      devType: val,
                      // Clear adapter if it no longer matches the new type
                      adapter_id:
                        prev.adapter_id &&
                        adapters.find(
                          (a) => a.id === prev.adapter_id && a.lift_type?.toUpperCase() === label,
                        )
                          ? prev.adapter_id
                          : "",
                    }));
                  }}
                  size="large"
                >
                  {BANK_DEV_TYPES.map((dt) => (
                    <Select.Option key={dt} value={dt}>
                      {DEV_TYPE_LABELS[dt]}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Row 2: Channel + Adapter */}
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Channel</span>
                </div>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={form.channel_id || undefined}
                  placeholder="Select channel"
                  onChange={(val) => setForm((prev) => ({ ...prev, channel_id: val }))}
                  size="large"
                >
                  {channels.map((ch) => (
                    <Select.Option key={ch.id} value={ch.id}>
                      {ch.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="col-12 col-md-6 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">Adapter</span>
                </div>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={form.adapter_id || undefined}
                  placeholder="Select adapter"
                  size="large"
                  onChange={async (val) => {
                    const ad = adapters.find((a) => a.id === val);
                    // The protocol-gated bank fields follow the adapter the
                    // moment it is picked, as they do when a saved bank is
                    // opened (handleEditBank). They used to be set only there,
                    // so a new OPC / OPC UA bank showed no vendor path inputs
                    // until it had been added and reopened, and a new MCE bank
                    // no Event Reporting Address.
                    const pickedProto = resolveAdapterProtocolUpper(ad);
                    const pickedOpcPath = OPC_PATH_PROTOCOLS.has(pickedProto);
                    setIsOpcPathProto(pickedOpcPath);
                    setIsMceProto(pickedProto === "MCE");
                    setForm((prev) => {
                      const patch: Partial<typeof prev> = { adapter_id: val };
                      // Auto-set devType from adapter's lift_type
                      if (ad?.lift_type) {
                        patch.devType = devTypeFromString(ad.lift_type);
                      }
                      // Segments typed for an OPC adapter mean nothing to any
                      // other protocol: drop them rather than keep hidden text
                      // that would reappear on switching back.
                      if (!pickedOpcPath) {
                        OPC_VENDOR_PATH_FIELDS.forEach((field: OpcVendorPathField) => {
                          patch[field] = null;
                        });
                      }
                      return { ...prev, ...patch };
                    });
                    setInitialSimpleBaseOffsets(null);

                    // Parse JSON string fields from FlatBuffer path
                    const parseSafe = (v: unknown) => {
                      if (!v) return null;
                      if (typeof v === "object") return v;
                      try {
                        return JSON.parse(v as string);
                      } catch {
                        return null;
                      }
                    };

                    // Fetch the full adapter (single-adapter endpoint includes
                    // CDO data that the list endpoint omits for performance).
                    let adWithParsed: any = ad
                      ? {
                          ...ad,
                          offset_mapping: parseSafe(ad.offset_mapping),
                          intra_content_flags: parseSafe(ad.intra_content_flags),
                          cdo_input: parseSafe(ad.cdo_input),
                          cdo_template: parseSafe(ad.cdo_template),
                        }
                      : ad;

                    // Reused by the OPC UA wiring below so that picking an
                    // adapter does not cost a second token fetch. Stays null
                    // when the detail fetch never ran or failed; the helper
                    // resolves its own token in that case.
                    let adapterToken: string | null = null;

                    if (val) {
                      try {
                        const token = await GETJwtToken();
                        adapterToken = token;
                        const [fullAdResult, keysResult] = await Promise.all([
                          triggerGetAdapter({ id: val, token }).unwrap(),
                          triggerGetAdapterKeys({ adapterId: val, token }).unwrap(),
                        ]);
                        const fullAd = fullAdResult?.data || fullAdResult;
                        if (fullAd) {
                          adWithParsed = {
                            ...adWithParsed,
                            ...fullAd,
                            offset_mapping:
                              parseSafe(fullAd.offsetMappingJson ?? fullAd.offset_mapping) ??
                              adWithParsed?.offset_mapping,
                            intra_content_flags:
                              parseSafe(
                                fullAd.intraContentFlagsJson ?? fullAd.intra_content_flags,
                              ) ?? adWithParsed?.intra_content_flags,
                            cdo_input:
                              parseSafe(fullAd.cdoInputJson ?? fullAd.cdo_input) ??
                              adWithParsed?.cdo_input,
                            cdo_template:
                              parseSafe(fullAd.cdoTemplateJson ?? fullAd.cdo_template) ??
                              adWithParsed?.cdo_template,
                            keys: keysResult?.data || fullAd.keys || adWithParsed?.keys || [],
                          };
                        } else {
                          adWithParsed = { ...adWithParsed, keys: keysResult?.data || [] };
                        }
                      } catch {
                        // Full adapter fetch failed — continue with list data
                      }
                    }

                    // Stash the full adapter so buildAdapterDefaults2Row sees its offset_mapping.
                    setCurrentBankAdapter(adWithParsed ?? null);

                    // Apply the same OPC UA state the bank-edit hydrate applies.
                    // This was the missing half: `isOpcUaProto` — which gates the
                    // node->lift matrix's render and the mapping guard in
                    // handleAddBank — was only ever set by handleEditBank, so
                    // creating an OPC UA bank showed no matrix at all and the
                    // operator had to add the bank, reopen it, and save twice.
                    await applyOpcUaAdapterState(
                      resolveAdapterProtocolUpper(adWithParsed),
                      val,
                      adapterToken,
                    );

                    // Load offset matrix from adapter. The SAME derivation the
                    // bank-edit path uses: this path used to assemble the
                    // inputs itself and omit ln1KeyMappings, which cost every
                    // column its direction and its real byte span, so the bank
                    // matrix flagged direction-split pairs the adapter matrix
                    // excused.
                    const types = applyAdapterMatrixInputs(deriveAdapterMatrixInputs(adWithParsed));
                    if (types.length > 0) {
                      // Pre-populate from adapter's per-unit offset_mapping
                      const numLifts = form.number_of_lifts || 1;
                      const derived = deriveOffsetsFromAdapter(adWithParsed, types, numLifts);
                      if (derived.length > 0) {
                        setLiftOffsets(derived);
                        setAdapterDefaultOffsets(derived);
                      } else {
                        const zeroRows = Array.from({ length: numLifts }, () => {
                          const row: Record<string, number> = {};
                          types.forEach((mt) => {
                            row[mt.categoryKey] = 0;
                          });
                          return row;
                        });
                        setLiftOffsets(zeroRows);
                        setAdapterDefaultOffsets(zeroRows);
                      }
                    } else {
                      setLiftOffsets([]);
                      setAdapterDefaultOffsets([]);
                      setRegisterTypeMappedTypes([]);
                      setCategoryToRegType({});
                    }
                  }}
                >
                  {adapters
                    .filter((ad) => !ad.lift_type || ad.lift_type.toUpperCase() === typeLabel)
                    .map((ad) => (
                      <Select.Option key={ad.id} value={ad.id}>
                        {ad.name}
                        {ad.lift_type ? ` (${ad.lift_type})` : ""}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            </div>

            {/* Blocking first: the save cannot go through until this pairing is
                fixed, unlike the two notices below it. */}
            {renderOpcUaEndpointAlert()}

            {renderRoutableMismatchWarning()}
            {renderNumatoSessionWarning()}

            {renderHeartbeatMappingWarning()}

            {/* Row 3: Lifts + (Floors + Floor Below Lobby for elevator types) */}
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputFlexRequired">*</span>
                  <span className="generalStyles__inputLabel">
                    Number of {FLOOR_DEV_TYPES.has(form.devType) ? "Lifts" : "Units"}
                  </span>
                </div>
                <InputNumber
                  size="large"
                  style={{ width: "100%", marginTop: 4 }}
                  // Lowering this below the bank's saved car count is allowed:
                  // saving then deletes the surplus cars behind a confirmation
                  // (see `selectSurplusLifts` / `confirmLiftReduction`), because
                  // a bank that declared fewer cars than it has rows is a
                  // desync, not a shrink. The floor is 1 either way — a bank
                  // with zero cars is not a bank.
                  min={editingBankId ? 1 : 0}
                  max={1024}
                  value={form.number_of_lifts}
                  onChange={(val) => {
                    const floor = editingBankId ? 1 : 0;
                    const count = Math.min(Math.max(val ?? 1, floor), 1024);
                    setForm((prev) => ({
                      ...prev,
                      number_of_lifts: count,
                    }));
                    if (mappedTypes.length > 0) {
                      // Pass the loaded adapter so resize can re-derive
                      // per-unit offsets for new rows from the adapter's
                      // offset_mapping. Without this hand-off, growing the
                      // bank from 1 lift to N produces zero stride for cars
                      // 2..N because stride inferred from a single row is 0
                      // — even when the adapter encoded a real stride.
                      // currentBankAdapter is the fully-resolved adapter
                      // (set when the user picked it from the dropdown); it
                      // has the parsed offset_mapping array required by the
                      // deriver. Falls back to stride extrapolation when
                      // the adapter handle isn't available yet.
                      setLiftOffsets((prev) =>
                        resizeLiftOffsetRows(prev, count, mappedTypes, currentBankAdapter),
                      );
                      setAdapterDefaultOffsets((prev) =>
                        resizeLiftOffsetRows(prev, count, mappedTypes, currentBankAdapter),
                      );
                    }
                  }}
                />
              </div>
              {FLOOR_DEV_TYPES.has(form.devType) && (
                <>
                  <div className="col-12 col-md-4 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">Floor Count</span>
                    </div>
                    <InputNumber
                      size="large"
                      style={{ width: "100%", marginTop: 4 }}
                      min={Math.max(2, form.floor_below_lobby + 1)}
                      max={1024}
                      value={form.floor_count}
                      onChange={(val) => {
                        const newVal = Math.min(val ?? 1, 1024);
                        setForm((prev) => ({
                          ...prev,
                          floor_count: newVal,
                          ...(prev.stopsLinked ? { stops: newVal } : {}),
                        }));
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-4 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">Floor Below Lobby</span>
                    </div>
                    <InputNumber
                      size="large"
                      style={{ width: "100%", marginTop: 4 }}
                      min={0}
                      value={form.floor_below_lobby}
                      onChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          floor_below_lobby: val ?? 0,
                        }))
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {/* Row 4: Start Floor + Stops + Logical Port (elevator types only for floor fields) */}
            <div className="row">
              {FLOOR_DEV_TYPES.has(form.devType) && (
                <>
                  <div className="col-12 col-md-4 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">Start Floor No</span>
                    </div>
                    <InputNumber
                      size="large"
                      style={{ width: "100%", marginTop: 4 }}
                      value={form.start_floor_no}
                      onChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          start_floor_no: val ?? 1,
                        }))
                      }
                    />
                  </div>
                  <div className="col-12 col-md-4 mt-3">
                    <div>
                      <span className="generalStyles__inputFlexRequired">*</span>
                      <span className="generalStyles__inputLabel">
                        Stops
                        {form.stopsLinked && (
                          <span style={{ opacity: 0.5, fontSize: 11, marginLeft: 4 }}>
                            (linked to Floor Count)
                          </span>
                        )}
                      </span>
                    </div>
                    <InputNumber
                      size="large"
                      style={{ width: "100%", marginTop: 4 }}
                      min={1}
                      value={form.stops}
                      disabled={form.stopsLinked}
                      onChange={(val) => setForm((prev) => ({ ...prev, stops: val ?? 1 }))}
                    />
                    <Switch
                      size="small"
                      checked={form.stopsLinked}
                      onChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          stopsLinked: checked,
                          ...(checked ? { stops: prev.floor_count } : {}),
                        }))
                      }
                      style={{ marginTop: 4 }}
                    />
                    <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.6 }}>
                      {form.stopsLinked ? "Linked" : "Independent"}
                    </span>
                  </div>
                </>
              )}
              <div className="col-12 col-md-4 mt-3">
                <span className="generalStyles__inputLabel">Logical Port</span>
                <InputNumber
                  size="large"
                  style={{ width: "100%", marginTop: 4 }}
                  min={logicalPortMin(assignedAdapterProtocol)}
                  max={logicalPortMax(assignedAdapterProtocol)}
                  status={logicalPortInvalid ? "error" : undefined}
                  value={form.logical_port}
                  placeholder="Not set"
                  onChange={(val) => setForm((prev) => ({ ...prev, logical_port: val }))}
                />
                {logicalPortInvalid && (
                  <Alert
                    type="error"
                    showIcon
                    message={logicalPortRangeMessage()}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
              {isMceProto && (
                <div className="col-12 col-md-4 mt-3">
                  <FormInputText
                    inputValue={form.event_reporting_address ?? ""}
                    inputLabelName="Event Reporting Address"
                    inputOnChange={onChangeFormValue}
                    inputVariableName="event_reporting_address"
                    isRequired={false}
                    disabledInput={false}
                    formularyType={FormularyType.WIZARD}
                  />
                </div>
              )}
              {isOpcPathProto &&
                OPC_VENDOR_PATH_FIELDS.map((field: OpcVendorPathField) => (
                  <div className="col-12 col-md-4 mt-3" key={field}>
                    <FormInputText
                      inputValue={form[field] ?? ""}
                      inputLabelName={OPC_VENDOR_PATH_LABELS[field]}
                      inputOnChange={onChangeFormValue}
                      inputVariableName={field}
                      isRequired={false}
                      disabledInput={false}
                      formularyType={FormularyType.WIZARD}
                      tooltip={t(OPC_VENDOR_PATH_HELP_KEYS[field])}
                    />
                  </div>
                ))}
              {isOpcPathProto && (
                <div className="col-12">
                  <OpcVendorPathFeedback
                    transition={opcVendorPathVerdict}
                    preview={opcVendorPathPreview}
                  />
                </div>
              )}
            </div>

            {/* Row 5: Front Doors / Rear Doors (elevator types only) / Visible. */}
            {/* Invariant: a bank must always have at least Front OR Rear */}
            {/* doors enabled. The handlers block the user from turning off */}
            {/* the last remaining one — they keep state untouched and show */}
            {/* a red error toast so the rule is discoverable. Visible rides */}
            {/* along in this same row when it's shown (this bank's devType */}
            {/* has floors and isn't showing the per-floor door table); the */}
            {/* standalone Visible row further below only renders when this */}
            {/* one doesn't, so the switch is never duplicated or hidden. */}
            {FLOOR_DEV_TYPES.has(form.devType) && !showBankFloorDoorTable && (
              <div className="row">
                <div className="col-12 col-md-4 mt-3">
                  <div>
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="generalStyles__inputLabel">Front Doors</span>
                  </div>
                  <Switch
                    checked={form.front_doors}
                    onChange={handleGlobalFrontDoorsChange}
                    style={{ marginTop: "10px" }}
                  />
                </div>
                <div className="col-12 col-md-4 mt-3">
                  <div>
                    <span className="generalStyles__inputFlexRequired">*</span>
                    <span className="generalStyles__inputLabel">Rear Doors</span>
                  </div>
                  <Switch
                    checked={form.rear_doors}
                    onChange={handleGlobalRearDoorsChange}
                    style={{ marginTop: "10px" }}
                  />
                </div>
                <div className="col-12 col-md-4 mt-3">
                  <div>
                    <span className="generalStyles__inputLabel">Visible</span>
                  </div>
                  <Switch
                    checked={form.visible}
                    onChange={(checked) => {
                      setForm((prev) => ({ ...prev, visible: checked }));
                    }}
                    style={{ marginTop: "10px" }}
                  />
                </div>
              </div>
            )}

            {showBankFloorDoorTable && (
              <div className="row">
                <div className="col-12 mt-3">
                  <div
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <span className="generalStyles__inputLabel">Bank Floors</span>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Tooltip
                        title={
                          bankFloorSortDesc
                            ? "Sorted by Position (descending) — click to invert"
                            : "Sorted by Position (ascending) — click to invert"
                        }
                      >
                        <Button
                          size="small"
                          data-doc-label="Bank Floors Sort Order"
                          icon={
                            bankFloorSortDesc ? (
                              <SortDescendingOutlined />
                            ) : (
                              <SortAscendingOutlined />
                            )
                          }
                          onClick={() => setBankFloorSortDesc((prev) => !prev)}
                        />
                      </Tooltip>
                      <span style={{ fontSize: 12, opacity: 0.8 }}>
                        {t("general.enableManualEditing")}
                      </span>
                      <Switch
                        data-doc-label="Bank Floors"
                        // Carried explicitly: the sort button above now sits
                        // between the "Bank Floors" caption and this switch, so
                        // the scanner can no longer read that caption from the
                        // neighbouring span. Without an explicit label the
                        // switch is re-sluged on every sibling insert, which
                        // silently orphans its description.
                        checked={manualBankFloorEditingEnabled}
                        onChange={handleManualBankFloorEditingToggle}
                      />
                    </div>
                  </div>
                  <Table
                    dataSource={sortedBankFloorDoorRows}
                    rowKey={(record) => record.key}
                    pagination={false}
                    className="my-custom-table"
                    // Row-hover background tint, same pattern as
                    // ServersTable/AdaptersTable/etc: `my-custom-table`'s CSS
                    // (.my-custom-table .ant-table-tbody > tr:hover > td in
                    // generalStyles.scss) does the hover tint on its own —
                    // rowClassName here only needs the ordinary zebra
                    // striping, not per-row "active" state (that was
                    // OrganizationsTable's own inline-edit highlight, which
                    // doesn't apply to this table).
                    rowClassName={(_record: BankFloorDoorRow, index: number) =>
                      getTableRowClass(index, theme)
                    }
                    size="small"
                    columns={[
                      {
                        title: "Position",
                        dataIndex: "position",
                        key: "position",
                        render: (_: number, record: BankFloorDoorRow) => (
                          <InputNumber
                            value={record.position}
                            disabled={!manualBankFloorEditingEnabled}
                            style={{ width: "100%" }}
                            onChange={(value) => {
                              const next = Number(value);
                              if (!Number.isFinite(next)) return;
                              setBankFloorDoorRows((prev) =>
                                prev.map((row) =>
                                  row.key === record.key ? { ...row, position: next } : row,
                                ),
                              );
                            }}
                          />
                        ),
                      },
                      {
                        title: t("general.floorIndex"),
                        dataIndex: "floorIndex",
                        key: "floorIndex",
                        render: (_: number, record: BankFloorDoorRow) => (
                          <InputNumber
                            value={record.floorIndex}
                            disabled={!manualBankFloorEditingEnabled}
                            style={{ width: "100%" }}
                            onChange={(value) => {
                              const next = Number(value);
                              if (!Number.isFinite(next)) return;
                              setBankFloorDoorRows((prev) =>
                                prev.map((row) =>
                                  row.key === record.key ? { ...row, floorIndex: next } : row,
                                ),
                              );
                            }}
                          />
                        ),
                      },
                      {
                        title: "Name",
                        dataIndex: "name",
                        key: "name",
                        render: (_: string, record: BankFloorDoorRow) => (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <Input
                              value={record.name}
                              disabled={!manualBankFloorEditingEnabled}
                              onChange={(event) => {
                                const next = event.target.value;
                                setBankFloorDoorRows((prev) =>
                                  prev.map((row) =>
                                    row.key === record.key ? { ...row, name: next } : row,
                                  ),
                                );
                              }}
                            />
                            <WizardIdHoverTag value={record.id}>
                              <FontAwesomeIcon
                                icon={faFingerprint}
                                style={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }}
                              />
                            </WizardIdHoverTag>
                          </div>
                        ),
                      },
                      {
                        title: (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>Front Door</span>
                            <Switch
                              checked={form.front_doors}
                              onChange={handleGlobalFrontDoorsChange}
                            />
                          </div>
                        ),
                        dataIndex: "frontDoor",
                        key: "frontDoor",
                        render: (_: boolean, record: BankFloorDoorRow) => (
                          <Switch
                            checked={record.frontDoor}
                            onChange={(checked) => {
                              // The header switch's checked state is derived
                              // from bankFloorDoorRows by the useEffect
                              // above — updating rows here is enough; it
                              // reacts in both directions (on when any row
                              // turns on, off when the last one turns off).
                              setBankFloorDoorRows((prev) =>
                                prev.map((row) =>
                                  row.key === record.key ? { ...row, frontDoor: checked } : row,
                                ),
                              );
                            }}
                          />
                        ),
                      },
                      {
                        title: (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span>Rear Door</span>
                            <Switch
                              checked={form.rear_doors}
                              onChange={handleGlobalRearDoorsChange}
                            />
                          </div>
                        ),
                        dataIndex: "rearDoor",
                        key: "rearDoor",
                        render: (_: boolean, record: BankFloorDoorRow) => (
                          <Switch
                            checked={record.rearDoor}
                            onChange={(checked) => {
                              // See the frontDoor column's onChange above —
                              // same derivation, same reasoning.
                              setBankFloorDoorRows((prev) =>
                                prev.map((row) =>
                                  row.key === record.key ? { ...row, rearDoor: checked } : row,
                                ),
                              );
                            }}
                          />
                        ),
                      },
                      {
                        title: "",
                        key: "delete",
                        width: 48,
                        render: (_: unknown, record: BankFloorDoorRow) => (
                          <WizardOpGate op="delete">
                            <Button
                              type="text"
                              danger
                              size="small"
                              disabled={deletingFloorId !== null || bankFloorDoorRows.length <= 1}
                              loading={deletingFloorId === record.id}
                              title={
                                bankFloorDoorRows.length <= 1
                                  ? t("general.deleteFloorLastFloor")
                                  : t("general.deleteFloor")
                              }
                              onClick={() => handleDeleteBankFloor(record)}
                              icon={<DeleteOutlined />}
                            />
                          </WizardOpGate>
                        ),
                      },
                    ]}
                  />
                </div>
              </div>
            )}

            {/* Row 6 (fallback): Visible — controls whether this bank appears
                in the monitoring dashboard. Invisible banks still send and
                receive LN2 messages; they are only hidden from the
                monitoring grid. Visible is unconditional (every bank type
                has it), unlike Front/Rear Doors — so when Row 5 above isn't
                rendering it (non-floor devType, or the per-floor door table
                is showing instead of the simple switches), it falls back to
                its own row here rather than disappearing. */}
            {!(FLOOR_DEV_TYPES.has(form.devType) && !showBankFloorDoorTable) && (
              <div className="row">
                <div className="col-12 col-md-4 mt-3">
                  <div>
                    <span className="generalStyles__inputLabel">Visible</span>
                  </div>
                  <Switch
                    checked={form.visible}
                    onChange={(checked) => {
                      setForm((prev) => ({ ...prev, visible: checked }));
                    }}
                    style={{ marginTop: "10px" }}
                  />
                </div>
              </div>
            )}

            {/* Row 7: Draw on bank (bank overlays). Directly under whichever
                Visible row rendered above, for every bank type: the bank
                usually set to draw on another is a hidden module bank. The
                captions come from constants/bankOverlay.ts, shared with the
                standalone bank form; the Selects carry data-doc-label so the
                control inventory names them by caption. */}
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputLabel">{OVERLAY_TARGET_LABEL}</span>
                </div>
                <Select
                  data-doc-label="Draw on bank"
                  style={{ width: "100%", marginTop: 4 }}
                  size="large"
                  allowClear
                  placeholder="None"
                  value={form.overlay_bank_id || undefined}
                  notFoundContent={
                    overlayDrawnOnBy ?? "No other bank on this server can be drawn on"
                  }
                  onChange={(val?: string) => {
                    // Clearing the target resets the lifts mode too, so a later
                    // target starts from the default rather than a stale choice.
                    setForm((prev) => ({
                      ...prev,
                      overlay_bank_id: val || null,
                      overlay_lifts: val ? prev.overlay_lifts : OverlayLifts.ALL_LIFTS,
                    }));
                  }}
                >
                  {overlayChoice.targets.map((target) => (
                    <Select.Option key={target.id} value={target.id}>
                      {target.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className="col-12 col-md-4 mt-3">
                <div>
                  <span className="generalStyles__inputLabel">{OVERLAY_LIFTS_LABEL}</span>
                </div>
                <Select
                  data-doc-label="Which lifts"
                  style={{ width: "100%", marginTop: 4 }}
                  size="large"
                  value={form.overlay_lifts}
                  disabled={!form.overlay_bank_id}
                  onChange={(val: OverlayLifts) => {
                    setForm((prev) => ({ ...prev, overlay_lifts: val }));
                  }}
                >
                  <Select.Option value={OverlayLifts.ALL_LIFTS}>Every car</Select.Option>
                  <Select.Option value={OverlayLifts.SAME_CAR_NUMBER}>The same car</Select.Option>
                </Select>
              </div>
              <div className="col-12 col-md-4 mt-3">
                <p style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>{OVERLAY_HELP}</p>
                {overlayDrawnOnBy && (
                  <p role="note" style={{ fontSize: 12, marginBottom: 0 }}>
                    {overlayDrawnOnBy}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* OFFSET MATRIX (shown when adapter has Modbus CDO mappings) */}
        {form.adapter_id && mappedTypes.length > 0 && form.number_of_lifts > 0 && (
          <>
            <br />
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              <BankOffsetMatrix
                lifts={Array.from({ length: form.number_of_lifts }, (_, i) => ({
                  id: String(i),
                  name: `${UNIT_LABEL_BY_TYPE[form.devType] || "Car"} ${i + 1}`,
                }))}
                mappedTypes={mappedTypes}
                offsets={liftOffsets}
                onOffsetsChange={setLiftOffsets}
                floorCount={form.floor_count}
                intraContent={intraContent}
                adapterKeys={adapterKeyInfos}
                ln1KeyMappings={ln1KeyMappingsForMatrix}
                intraContentFlags={intraContentFlags}
                onOverlapChange={setBankHasOverlaps}
                disabledColumns={disabledColumns}
                onDisabledColumnsChange={setDisabledColumns}
                adapterDefaults={
                  adapterDefaultOffsets.length > 0 ? adapterDefaultOffsets : undefined
                }
                initialSimpleBaseOffsets={initialSimpleBaseOffsets}
                registerTypeMappedTypes={
                  registerTypeMappedTypes.length > 0 ? registerTypeMappedTypes : undefined
                }
                categoryToRegType={
                  Object.keys(categoryToRegType).length > 0 ? categoryToRegType : undefined
                }
                adapterDefaults2Row={buildAdapterDefaults2Row(
                  currentBankAdapter ?? adapters.find((a) => a.id === form.adapter_id),
                  mappedTypes,
                )}
              />
            </div>
          </>
        )}
        {/* PIN ASSIGNMENT MATRIX (shown for Sequent/Numato adapters) */}
        {form.adapter_id && isPinProto && editingBankId && form.number_of_lifts > 0 && (
          <>
            <br />
            {renderPinAssignmentAlerts()}
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              {bankLifts.length === 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message="This bank has no lifts yet"
                  description="Pins can't be assigned until this bank's lifts exist. Save the bank with its car count, then reopen it here to assign pins."
                />
              ) : (
                <BankPinAssignmentMatrix
                  allEntries={pinEntries}
                  onEntriesChange={setPinEntries}
                  bankId={editingBankId}
                  bankName={form.name}
                  lifts={bankLifts}
                  otherBanks={createdBanks
                    .filter((b) => b.id !== editingBankId)
                    .map((b): PinAssignmentBankOption => ({ id: b.id, name: b.name }))}
                  showMergedChannelHint={bankLifts.length > 1}
                  onRequestMergedChannels={() => goToStep(WizardStep.Channels)}
                />
              )}
            </div>
          </>
        )}
        {/* NODE -> LIFT MATRIX (shown for OPC UA adapters) */}
        {form.adapter_id && isOpcUaProto && form.number_of_lifts > 0 && (
          <>
            <br />
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              <BankNodeLiftMatrix
                nodeToMsg={opcUaAdapterMapping?.nodeToMsg ?? []}
                msgToNode={opcUaAdapterMapping?.msgToNode ?? []}
                monitors={opcUaAdapterMapping?.monitors ?? []}
                bindings={opcUaNodeLiftMapping}
                onBindingsChange={setOpcUaNodeLiftMapping}
                lifts={Array.from({ length: form.number_of_lifts }, (_, i) => ({
                  id: String(i),
                  name: `${UNIT_LABEL_BY_TYPE[form.devType] || "Car"} ${i + 1}`,
                }))}
              />
            </div>
          </>
        )}

        {!standalone && createdBanks.length > 0 && (
          <>
            <br />
            <DrawerRowDivider icon={faBuildingShield} title="Created Banks" />
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              {/* CREATED BANKS TABLE (hidden in standalone edit mode) */}
              <Table
                dataSource={createdBanks.map((b) => ({ ...b, key: b.id }))}
                columns={tableColumns}
                pagination={false}
                size="small"
                locale={{ emptyText: "No banks created yet" }}
              />
            </div>
          </>
        )}

        {/* DELETED BANKS (collapsed; hidden in standalone edit mode). Sits outside
            the "Created Banks" block above because a bank whose deletion left the
            table empty is exactly the one worth restoring. */}
        {!standalone && (
          <DeletedBanksPanel
            orgId={wizardState.organizationId ?? ""}
            serverId={wizardState.serverId ?? undefined}
            resolveProtocol={(adapterId) =>
              resolveAdapterProtocolUpper(adapters.find((a) => a.id === adapterId))
            }
            onRestored={handleBankRestored}
            onError={setStepError}
          />
        )}

        <Divider />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                <div className="generalStyles__mrFix">
                  <WizardExportButton stage={4} orgId={wizardState.organizationId} />
                </div>
                <div className="generalStyles__mrFix">
                  {editingBankId && (
                    <Button onClick={cancelEdit} size="large">
                      Cancel Edit
                    </Button>
                  )}
                </div>
                <div className="generalStyles__mrFix">
                  <Button
                    type="primary"
                    onClick={editingBankId ? handleUpdateBank : handleAddBank}
                    disabled={
                      !isFormValid() ||
                      bankHasOverlaps ||
                      (editingBankId ? !access.update : !access.create)
                    }
                    loading={isLoadingPost || isLoadingPut}
                    size="large"
                  >
                    {editingBankId ? "Update Bank" : "+ Add Bank"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ALERTS */}
      <GlobalAlert2
        isError={isErrorPost}
        isSuccess={isSuccessPost}
        requestType="POST"
        error={errorPost}
        name="Bank"
      />
    </Spin>
  );
}
